import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  indexArray,
  Match,
  on,
  onMount,
  Show,
  Switch,
  onCleanup,
  Index,
  type JSX,
} from "solid-js"
import { createStore } from "solid-js/store"
import stripAnsi from "strip-ansi"
import { Dynamic } from "solid-js/web"
import {
  AgentPart,
  AssistantMessage,
  FilePart,
  Message as MessageType,
  Part as PartType,
  ReasoningPart,
  Session,
  TextPart,
  ToolPart,
  UserMessage,
  Todo,
  QuestionAnswer,
  QuestionInfo,
} from "@opencode-ai/sdk/v2"
import { useData } from "../context"
import { useFileComponent } from "../context/file"
import { useArtifactPreview } from "../context/artifact-preview"
import { ReviewFindings } from "./review-findings"
import type { ReviewFinding } from "../context/review-actions"
import { useDialog } from "../context/dialog"
import { type UiI18n, useI18n } from "../context/i18n"
import { useChatLinkTitles } from "../context/chat-link-titles"
import { useArtifactPaths } from "../context/artifact-paths"
import { useMessageExtraAction } from "../context/message-actions"
import { BasicTool, GenericTool } from "./basic-tool"
import { type ToolOutcome, toolOutcome, toolOutcomeText } from "./tool-outcome"
import { Accordion } from "./accordion"
import { StickyAccordionHeader } from "./sticky-accordion-header"
import { Collapsible } from "./collapsible"
import { FileIcon } from "./file-icon"
import { commandProducesFiles } from "./file-work"
import { Icon } from "./icon"
import { Checkbox } from "./checkbox"
import { DiffChanges } from "./diff-changes"
import { chatSessionLinkPattern, chatSessionLinkText, Markdown } from "./markdown"
import { ImagePreview } from "./image-preview"
import { TextPreview } from "./text-preview"
import { attachmentFileHref } from "./attachment-chip"
import { Tooltip } from "./tooltip"
import { getDirectory as _getDirectory, getFilename } from "@opencode-ai/core/util/path"
import { Binary } from "@opencode-ai/core/util/binary"
import { checksum } from "@opencode-ai/core/util/encode"
import { IconButton } from "./icon-button"
import { TextShimmer } from "./text-shimmer"
import { ToolStatusTitle } from "./tool-status-title"
import { formatElapsed, formatTokenCount, sessionSpend, taskAgent } from "./task-insights"
import { patchFiles } from "./apply-patch-file"
import { text as diffText } from "./session-diff"
import { animate } from "motion"
import { useLocation } from "@solidjs/router"
import { attached, inline, kind, stripped } from "./message-file"

const [calendarNow, setCalendarNow] = createSignal(new Date())
const calendarRefresh = {
  subscribers: 0,
  timer: undefined as number | undefined,
}

function scheduleCalendarRefresh() {
  if (typeof window === "undefined") return
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setHours(24, 0, 0, 0)
  calendarRefresh.timer = window.setTimeout(
    () => {
      setCalendarNow(new Date())
      scheduleCalendarRefresh()
    },
    tomorrow.getTime() - now.getTime() + 1_000,
  )
}

function useCalendarNow() {
  onMount(() => {
    calendarRefresh.subscribers++
    if (calendarRefresh.subscribers > 1) return
    setCalendarNow(new Date())
    scheduleCalendarRefresh()
  })
  onCleanup(() => {
    calendarRefresh.subscribers--
    if (calendarRefresh.subscribers > 0) return
    if (typeof window !== "undefined" && calendarRefresh.timer !== undefined) window.clearTimeout(calendarRefresh.timer)
    calendarRefresh.timer = undefined
  })
  return calendarNow
}

function ShellSubmessage(props: { text: string; animate?: boolean; shimmer?: boolean }) {
  let widthRef: HTMLSpanElement | undefined
  let valueRef: HTMLSpanElement | undefined

  onMount(() => {
    if (!props.animate) return
    requestAnimationFrame(() => {
      if (widthRef) {
        animate(widthRef, { width: "auto" }, { type: "spring", visualDuration: 0.25, bounce: 0 })
      }
      if (valueRef) {
        animate(valueRef, { opacity: 1, filter: "blur(0px)" }, { duration: 0.32, ease: [0.16, 1, 0.3, 1] })
      }
    })
  })

  return (
    <span data-component="shell-submessage">
      <span ref={widthRef} data-slot="shell-submessage-width" style={{ width: props.animate ? "0px" : undefined }}>
        <span data-slot="basic-tool-tool-subtitle">
          <span
            ref={valueRef}
            data-slot="shell-submessage-value"
            style={props.animate ? { opacity: 0, filter: "blur(2px)" } : undefined}
          >
            <TextShimmer text={props.text} active={props.shimmer} />
          </span>
        </span>
      </span>
    </span>
  )
}

interface Diagnostic {
  range: {
    start: { line: number; character: number }
    end: { line: number; character: number }
  }
  message: string
  severity?: number
}

function getDiagnostics(
  diagnosticsByFile: Record<string, Diagnostic[]> | undefined,
  filePath: string | undefined,
): Diagnostic[] {
  if (!diagnosticsByFile || !filePath) return []
  const diagnostics = diagnosticsByFile[filePath] ?? []
  return diagnostics.filter((d) => d.severity === 1).slice(0, 3)
}

function DiagnosticsDisplay(props: { diagnostics: Diagnostic[] }): JSX.Element {
  const i18n = useI18n()
  return (
    <Show when={props.diagnostics.length > 0}>
      <div data-component="diagnostics">
        <For each={props.diagnostics}>
          {(diagnostic) => (
            <div data-slot="diagnostic">
              <span data-slot="diagnostic-label">{i18n.t("ui.messagePart.diagnostic.error")}</span>
              <span data-slot="diagnostic-location">
                [{diagnostic.range.start.line + 1}:{diagnostic.range.start.character + 1}]
              </span>
              <span data-slot="diagnostic-message">{diagnostic.message}</span>
            </div>
          )}
        </For>
      </div>
    </Show>
  )
}

export interface MessageProps {
  message: MessageType
  parts: PartType[]
  actions?: UserActions
  showAssistantCopyPartID?: string | null
  showReasoningSummaries?: boolean
}

export type SessionAction = (input: { sessionID: string; messageID: string }) => Promise<void> | void

export type UserActions = {
  revert?: SessionAction
  // Re-run a FAILED turn: drop the dead assistant turn and re-send the same
  // user prompt. Offered by the error card in session-turn.
  retry?: SessionAction
  // Daily-budget error card only (the gateway's per-install DeepSeek day
  // budget): re-send the same prompt on GigaChat instead. Offered when the
  // failed turn ran on DeepSeek and the app has a GigaChat provider.
  switchToGigaChat?: SessionAction
  // Daily-budget error card only: queue the same prompt to go out once the
  // budget resets. `resetAt` is the instant the error body named (epoch ms),
  // absent when it carried none — the app then falls back to its own reading.
  sendAtReset?: (input: { sessionID: string; messageID: string; resetAt?: number }) => Promise<void> | void
  // Too-large card only (a context overflow the sidecar could not compact its
  // way out of): open a new chat carrying a short summary of this one. Retry
  // is hidden there — it would send the same oversized history again.
  startNewChat?: SessionAction
}

export interface MessagePartProps {
  part: PartType
  message: MessageType
  hideDetails?: boolean
  defaultOpen?: boolean
  showAssistantCopyPartID?: string | null
  turnDurationMs?: number
  suppressArtifactPreviews?: boolean
  // GigaTool: basenames of files written/edited in this turn, threaded down to
  // text/reasoning markdown so inline-code mentions of them (e.g. `deck.js`)
  // become clickable regardless of extension. See Markdown.artifactFilenames.
  artifactFilenames?: string[]
  // GigaTool: whether this turn ran a tool that produces files. Decides what a
  // named-but-absent file means — see turnAttemptedFileWork.
  attemptedFileWork?: boolean
}

export type PartComponent = Component<MessagePartProps>

export const PART_MAPPING: Record<string, PartComponent | undefined> = {}

const TEXT_RENDER_PACE_MS = 24
const TEXT_RENDER_SNAP = /[\s.,!?;:)\]]/

function step(size: number) {
  if (size <= 12) return 2
  if (size <= 48) return 4
  if (size <= 96) return 8
  return Math.min(24, Math.ceil(size / 8))
}

function next(text: string, start: number) {
  const end = Math.min(text.length, start + step(text.length - start))
  const max = Math.min(text.length, end + 8)
  for (let i = end; i < max; i++) {
    if (TEXT_RENDER_SNAP.test(text[i] ?? "")) return i + 1
  }
  return end
}

function createPacedValue(getValue: () => string, live?: () => boolean) {
  const [value, setValue] = createSignal(getValue())
  let shown = getValue()
  let timeout: ReturnType<typeof setTimeout> | undefined

  const clear = () => {
    if (!timeout) return
    clearTimeout(timeout)
    timeout = undefined
  }

  const sync = (text: string) => {
    shown = text
    setValue(text)
  }

  const run = () => {
    timeout = undefined
    const text = getValue()
    if (!live?.()) {
      sync(text)
      return
    }
    if (!text.startsWith(shown) || text.length <= shown.length) {
      sync(text)
      return
    }
    const end = next(text, shown.length)
    sync(text.slice(0, end))
    if (end < text.length) timeout = setTimeout(run, TEXT_RENDER_PACE_MS)
  }

  createEffect(() => {
    const text = getValue()
    if (!live?.()) {
      clear()
      sync(text)
      return
    }
    if (!text.startsWith(shown) || text.length < shown.length) {
      clear()
      sync(text)
      return
    }
    if (text.length === shown.length || timeout) return
    timeout = setTimeout(run, TEXT_RENDER_PACE_MS)
  })

  onCleanup(() => {
    clear()
  })

  return value
}

function PacedMarkdown(props: {
  text: string
  cacheKey: string
  streaming: boolean
  linkArtifacts?: boolean
  artifactFilenames?: string[]
}) {
  const value = createPacedValue(
    () => props.text,
    () => props.streaming,
  )

  return (
    <Show when={value()}>
      <Markdown
        text={value()}
        cacheKey={props.cacheKey}
        streaming={props.streaming}
        linkArtifacts={props.linkArtifacts}
        artifactFilenames={props.artifactFilenames}
      />
    </Show>
  )
}

function relativizeProjectPath(path: string, directory?: string) {
  if (!path) return ""
  if (!directory) return path
  if (directory === "/") return path
  if (directory === "\\") return path
  if (path === directory) return ""

  const separator = directory.includes("\\") ? "\\" : "/"
  const prefix = directory.endsWith(separator) ? directory : directory + separator
  if (!path.startsWith(prefix)) return path
  return path.slice(directory.length)
}

function getDirectory(path: string | undefined) {
  const data = useData()
  return relativizeProjectPath(_getDirectory(path), data.directory)
}

import type { IconProps } from "./icon"
import { formatDurationLabel } from "./elapsed"
import { AutoApprovedMark, autoApprovalOf } from "./auto-approved"

export type ToolInfo = {
  icon: IconProps["name"]
  title: string
  subtitle?: string
}

function agentTitle(i18n: UiI18n, type?: string) {
  if (!type) return i18n.t("ui.tool.agent.default")
  return i18n.t("ui.tool.agent", { type })
}

// Agent color assignment and token formatting are shared with the app's
// agents panel — see task-insights.tsx.

export function getToolInfo(tool: string, input: any = {}): ToolInfo {
  const i18n = useI18n()
  switch (tool) {
    case "read":
      return {
        icon: "file-glass",
        title: i18n.t("ui.tool.read"),
        subtitle: input.filePath ? getFilename(input.filePath) : undefined,
      }
    case "list":
      return {
        icon: "numbered-view",
        title: i18n.t("ui.tool.list"),
        subtitle: input.path ? getFilename(input.path) : undefined,
      }
    case "glob":
      return {
        icon: "file-glass",
        title: i18n.t("ui.tool.glob"),
        subtitle: input.pattern,
      }
    case "grep":
      return {
        icon: "magnifying-glass",
        title: i18n.t("ui.tool.grep"),
        subtitle: input.pattern,
      }
    case "webfetch":
      return {
        icon: "file-glass",
        title: i18n.t("ui.tool.webfetch"),
        subtitle: input.url,
      }
    case "websearch":
      return {
        icon: "globe",
        title: i18n.t("ui.tool.websearch"),
        subtitle: input.query,
      }
    case "task": {
      const type =
        typeof input.subagent_type === "string" && input.subagent_type
          ? input.subagent_type[0]!.toUpperCase() + input.subagent_type.slice(1)
          : undefined
      return {
        icon: "robot",
        title: agentTitle(i18n, type),
        subtitle: input.description,
      }
    }
    case "bash":
      return {
        icon: "terminal",
        title: i18n.t("ui.tool.shell"),
        subtitle: input.description,
      }
    case "edit":
      return {
        icon: "edit-outline",
        title: i18n.t("ui.messagePart.title.edit"),
        subtitle: input.filePath ? getFilename(input.filePath) : undefined,
      }
    case "write":
      return {
        icon: "edit-outline",
        title: i18n.t("ui.messagePart.title.write"),
        subtitle: input.filePath ? getFilename(input.filePath) : undefined,
      }
    case "apply_patch":
      return {
        icon: "wrench",
        title: i18n.t("ui.tool.patch"),
        subtitle: input.files?.length
          ? `${input.files.length} ${i18n.t(input.files.length > 1 ? "ui.common.file.other" : "ui.common.file.one")}`
          : undefined,
      }
    case "todowrite":
      return {
        icon: "checklist",
        title: i18n.t("ui.tool.todos"),
      }
    case "question":
      return {
        icon: "ear",
        title: i18n.t("ui.tool.questions"),
      }
    case "skill":
      return {
        icon: "book-open",
        title: input.name || i18n.t("ui.tool.skill"),
      }
    case "report_findings":
      return {
        icon: "review-active",
        title: i18n.t("ui.tool.reportFindings"),
      }
    default:
      return {
        icon: "hammer",
        title: tool,
      }
  }
}

function urls(text: string | undefined) {
  if (!text) return []
  const seen = new Set<string>()
  return [...text.matchAll(/https?:\/\/[^\s<>"'`)\]]+/g)]
    .map((item) => item[0].replace(/[),.;:!?]+$/g, ""))
    .filter((item) => {
      if (seen.has(item)) return false
      seen.add(item)
      return true
    })
}

function sessionLink(id: string | undefined, path: string, href?: (id: string) => string | undefined) {
  if (!id) return

  const direct = href?.(id)
  if (direct) return direct

  const idx = path.indexOf("/session")
  if (idx === -1) return
  return `${path.slice(0, idx)}/session/${id}`
}

function currentSession(path: string) {
  return path.match(/\/session\/([^/?#]+)/)?.[1]
}

function taskSession(
  input: Record<string, any>,
  path: string,
  sessions: Session[] | undefined,
  agents?: readonly { name: string; color?: string }[],
) {
  const parentID = currentSession(path)
  if (!parentID) return
  const description = typeof input.description === "string" ? input.description : ""
  const agent = taskAgent(input.subagent_type, agents).name
  return (sessions ?? [])
    .filter((session) => session.parentID === parentID && !session.time?.archived)
    .filter((session) => (description ? session.title.startsWith(description) : true))
    .filter((session) => (agent ? session.title.includes(`@${agent}`) : true))
    .sort((a, b) => (b.time.created ?? 0) - (a.time.created ?? 0))[0]?.id
}

const CONTEXT_GROUP_TOOLS = new Set(["read", "glob", "grep", "list", "bash"])

// A pending part has no metadata yet; every later state may carry some.
function partMetadata(part: ToolPart): Record<string, unknown> | undefined {
  return "metadata" in part.state ? (part.state.metadata as Record<string, unknown> | undefined) : undefined
}
const HIDDEN_TOOLS = new Set(["todowrite"])
const MAX_ACTIVITY_ITEMS = 2
const CONTEXT_GROUP_MIN_ITEMS = 4
const TASK_GROUP_MIN_ITEMS = 2

function isTaskToolPart(part: PartType): part is ToolPart {
  return part.type === "tool" && part.tool === "task"
}

function list<T>(value: T[] | undefined | null, fallback: T[]) {
  if (Array.isArray(value)) return value
  return fallback
}

function same<T>(a: readonly T[] | undefined, b: readonly T[] | undefined) {
  if (a === b) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false
  return a.every((x, i) => x === b[i])
}

type PartRef = {
  messageID: string
  partID: string
}

type PartGroup =
  | {
      key: string
      type: "part"
      ref: PartRef
    }
  | {
      key: string
      type: "context"
      refs: PartRef[]
    }
  | {
      key: string
      type: "tasks"
      refs: PartRef[]
    }

function sameRef(a: PartRef, b: PartRef) {
  return a.messageID === b.messageID && a.partID === b.partID
}

function sameGroup(a: PartGroup, b: PartGroup) {
  if (a === b) return true
  if (a.key !== b.key) return false
  if (a.type !== b.type) return false
  if (a.type === "part") {
    if (b.type !== "part") return false
    return sameRef(a.ref, b.ref)
  }
  if (b.type === "part") return false
  if (a.refs.length !== b.refs.length) return false
  return a.refs.every((ref, i) => sameRef(ref, b.refs[i]!))
}

function sameGroups(a: readonly PartGroup[] | undefined, b: readonly PartGroup[] | undefined) {
  if (a === b) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false
  return a.every((item, i) => sameGroup(item, b[i]!))
}

function groupParts(parts: { messageID: string; part: PartType }[], opts?: { taskGroups?: boolean }) {
  const result: PartGroup[] = []
  let start = -1
  let taskStart = -1

  const pushPart = (item: { messageID: string; part: PartType }) =>
    result.push({
      key: `part:${item.messageID}:${item.part.id}`,
      type: "part",
      ref: {
        messageID: item.messageID,
        partID: item.part.id,
      },
    })

  const flush = (end: number) => {
    if (start < 0) return
    const first = parts[start]
    const group = parts.slice(start, end + 1)
    if (!first || group.length === 0) {
      start = -1
      return
    }
    if (group.length < CONTEXT_GROUP_MIN_ITEMS) {
      group.forEach(pushPart)
      start = -1
      return
    }
    result.push({
      key: `context:${first.part.id}`,
      type: "context",
      refs: group.map((item) => ({
        messageID: item.messageID,
        partID: item.part.id,
      })),
    })
    start = -1
  }

  // A run of ≥2 adjacent subagent (task) calls — the model fanning out in
  // parallel — collapses into one fleet widget; a lone task keeps its
  // regular per-part rendering.
  const flushTasks = (end: number) => {
    if (taskStart < 0) return
    const first = parts[taskStart]
    const group = parts.slice(taskStart, end + 1)
    if (!first || group.length === 0) {
      taskStart = -1
      return
    }
    if (group.length < TASK_GROUP_MIN_ITEMS) {
      group.forEach(pushPart)
      taskStart = -1
      return
    }
    result.push({
      key: `tasks:${first.part.id}`,
      type: "tasks",
      refs: group.map((item) => ({
        messageID: item.messageID,
        partID: item.part.id,
      })),
    })
    taskStart = -1
  }

  parts.forEach((item, index) => {
    if (isContextGroupTool(item.part)) {
      flushTasks(index - 1)
      if (start < 0) start = index
      return
    }

    if (opts?.taskGroups && isTaskToolPart(item.part)) {
      flush(index - 1)
      if (taskStart < 0) taskStart = index
      return
    }

    flush(index - 1)
    flushTasks(index - 1)
    pushPart(item)
  })

  flush(parts.length - 1)
  flushTasks(parts.length - 1)
  return result
}

function index<T extends { id: string }>(items: readonly T[]) {
  return new Map(items.map((item) => [item.id, item] as const))
}

export function renderable(part: PartType, showReasoningSummaries = true) {
  if (part.type === "tool") {
    if (HIDDEN_TOOLS.has(part.tool)) return false
    if (part.tool === "question") return part.state.status !== "pending" && part.state.status !== "running"
    return true
  }
  if (part.type === "text") return !!part.text?.trim()
  if (part.type === "reasoning") return showReasoningSummaries && !!part.text?.trim()
  return !!PART_MAPPING[part.type]
}

// A saved-memory tool part renders as the "🧠 Remembered · Undo" chip — a
// persistent confirmation the user acts on *after* the turn. It must survive
// both the thinking-layer auto-collapse (see session-turn.tsx) and the
// "hide activity once the answer arrives" cull below. Running / errored /
// "nothing to remember" memory parts have no chip and are treated as normal
// tool activity.
export function isMemoryChipPart(part: PartType | undefined) {
  return (
    part?.type === "tool" &&
    part.tool === "memory" &&
    part.state.status === "completed" &&
    part.state.metadata?.saved === true
  )
}

function toolDefaultOpen(tool: string, shell = false, edit = false) {
  if (tool === "bash") return shell
  if (tool === "edit" || tool === "write" || tool === "apply_patch") return edit
}

function partDefaultOpen(part: PartType, shell = false, edit = false) {
  if (part.type !== "tool") return
  return toolDefaultOpen(part.tool, shell, edit)
}

function artifactBasename(value: string) {
  return value.replace(/\\/g, "/").split("/").pop() ?? value
}

// GigaTool: basenames of files the given parts wrote or edited. Threaded into
// the text/reasoning markdown so inline-code mentions of them (e.g. `deck.js`)
// are linkified regardless of extension — and only then, so we never linkify a
// name we can't open. See Markdown.artifactFilenames.
//
// Backslashes are normalized first so a Windows path (C:\Users\me\deck.js)
// yields `deck.js`, not the whole string — matching the click-time resolver in
// session.tsx. Scope is write/edit only, the same tools that resolver keys off,
// so every linkified name has a reliable path to reveal. apply_patch is
// intentionally excluded: it edits existing files rather than producing new
// artifacts, and its paths live in output metadata, not input.filePath.
function collectArtifactFilenames(parts: readonly PartType[]): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  for (const part of parts) {
    if (part.type !== "tool") continue
    if (part.tool !== "write" && part.tool !== "edit") continue
    const filePath = (part.state.input as Record<string, unknown> | undefined)?.filePath
    if (typeof filePath !== "string") continue
    const base = artifactBasename(filePath)
    if (!base || seen.has(base)) continue
    seen.add(base)
    names.push(base)
  }
  return names
}

// GigaTool: did this turn actually TRY to produce a file? A model naming a
// file it never wrote is two different things depending on the answer. With no
// file-producing tool in the turn, `foo.xlsx` in the text is an example, and
// the artifact chip is right to stay silent. With one — a `write`, an `edit`,
// or a shell command, which is how every office skill builds its output — a
// named file that is not on disk is the failure mode from feedback #139: the
// run threw, the model reported "файл создан" anyway, and the only signal the
// user got was the quiet absence of a chip.
//
// Only a SHELL command that could have written a file counts, and this is the
// narrow reading on purpose.
//
// `write`/`edit` are deliberately absent: those tools report their own failure
// as a part in the chat, so a file they were supposed to produce is never
// silently missing — and a turn that wrote a.md while mentioning b.xlsx as an
// example would otherwise warn about b.xlsx. What has no such report is a
// script run through the shell, which is how every office skill builds its
// output: deck.js throws, the turn looks ordinary, and the model says «Файл
// создан» (feedback #139).
//
// Reading commands are excluded: a turn that runs `ls` (or `Get-Item main.py`)
// and then correctly answers whether the file is there must not decorate its
// own true answer with a warning that it was not created. See file-work.ts.
function turnAttemptedFileWork(parts: readonly PartType[]): boolean {
  return parts.some((part) => {
    if (part.type !== "tool" || part.tool !== "bash") return false
    const input = part.state.input as Record<string, unknown> | undefined
    const command = typeof input?.command === "string" ? input.command : ""
    return commandProducesFiles(command)
  })
}

const ARTIFACT_FILE_PATH =
  /\/(?:Users|Volumes|Applications|Library|System|private|tmp|var|opt|usr|etc|home)\/[^\s'"`<>]+\.(?:pdf|csv|tsv|xlsx?|pptx?|docx?|md|markdown|html?|json|py|png|jpe?g|gif|webp)\b/gi
const ARTIFACT_INLINE_CODE =
  /`([^\s`]+\.(?:pdf|csv|tsv|xlsx?|pptx?|docx?|md|markdown|html?|json|py|png|jpe?g|gif|webp))`/gi

function collectArtifactPathsFromText(text: string): string[] {
  if (!text) return []
  const seen = new Set<string>()
  const out: string[] = []
  let m: RegExpExecArray | null
  const absRe = new RegExp(ARTIFACT_FILE_PATH.source, ARTIFACT_FILE_PATH.flags)
  while ((m = absRe.exec(text)) !== null) {
    if (seen.has(m[0])) continue
    seen.add(m[0])
    out.push(m[0])
  }
  const codeRe = new RegExp(ARTIFACT_INLINE_CODE.source, ARTIFACT_INLINE_CODE.flags)
  while ((m = codeRe.exec(text)) !== null) {
    const basename = m[1]
    // Skip if any extracted absolute path already covers this basename —
    // avoids rendering two chips for the same file ("the absolute path chip" +
    // "the inline-code chip").
    if ([...seen].some((p) => p.endsWith("/" + basename) || p === basename)) continue
    seen.add(basename)
    out.push(basename)
  }
  return out
}

const sameStrings = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i])

function isActivityGroup(entry: PartGroup, parts: Map<string, Map<string, PartType>>) {
  // A fleet of parallel subagents is durable for the same reason a single
  // task part is (see below) — never cull it as ephemeral activity.
  if (entry.type === "tasks") return false
  if (entry.type === "context") return true
  const part = parts.get(entry.ref.messageID)?.get(entry.ref.partID)
  // `task` (subagent) parts are durable, navigable artifacts — the user wants to
  // see every subagent that was spawned and click into its session. Treating
  // them as ephemeral activity caps how many are visible while working (only the
  // last MAX_ACTIVITY_ITEMS) and hides them entirely once the final answer text
  // arrives — so a deep-research run that fans out to 6 web-researcher subagents
  // shows none of them. Keep task parts out of the activity bucket so they
  // always render.
  if (part?.type === "tool" && part.tool === "task") return false
  // The saved-memory chip is durable like task parts — keep it out of the
  // activity bucket so it isn't culled when the final answer arrives.
  if (isMemoryChipPart(part)) return false
  return part?.type === "tool"
}

export function AssistantParts(props: {
  messages: AssistantMessage[]
  showAssistantCopyPartID?: string | null
  turnDurationMs?: number
  working?: boolean
  showReasoningSummaries?: boolean
  shellToolDefaultOpen?: boolean
  editToolDefaultOpen?: boolean
  includePart?: (part: PartType) => boolean
  showAllActivity?: boolean
  suppressArtifactPreviews?: boolean
}) {
  const data = useData()
  const emptyParts: PartType[] = []
  const emptyTools: ToolPart[] = []
  const msgs = createMemo(() => index(props.messages))
  const part = createMemo(
    () =>
      new Map(
        props.messages.map((message) => [message.id, index(list(data.store.part?.[message.id], emptyParts))] as const),
      ),
  )
  const allParts = createMemo(() => props.messages.flatMap((m) => list(data.store.part?.[m.id], emptyParts)))

  const grouped = createMemo(
    () =>
      groupParts(
        props.messages.flatMap((message) =>
          list(data.store.part?.[message.id], emptyParts)
            .filter((part) => renderable(part, props.showReasoningSummaries ?? true))
            .filter((part) => props.includePart?.(part) ?? true)
            .map((part) => ({
              messageID: message.id,
              part,
            })),
        ),
        { taskGroups: data.taskGroups?.() ?? false },
      ),
    [] as PartGroup[],
    { equals: sameGroups },
  )
  const hasAnswer = createMemo(() =>
    props.messages.some((message) =>
      list(data.store.part?.[message.id], emptyParts).some((part) => part.type === "text" && !!part.text?.trim()),
    ),
  )
  const visibleGrouped = createMemo(
    () => {
      const entries = grouped()
      const parts = part()
      if (props.showAllActivity) return entries
      if (hasAnswer() && !props.working) return entries.filter((entry) => !isActivityGroup(entry, parts))

      const visibleActivityKeys = new Set(
        entries
          .filter((entry) => isActivityGroup(entry, parts))
          .map((entry) => entry.key)
          .slice(-MAX_ACTIVITY_ITEMS),
      )

      return entries.filter((entry) => !isActivityGroup(entry, parts) || visibleActivityKeys.has(entry.key))
    },
    [] as PartGroup[],
    { equals: sameGroups },
  )

  const last = createMemo(() => visibleGrouped().at(-1)?.key)

  // GigaTool: basenames of every file the model wrote/edited across this turn's
  // messages, so an inline-code mention of one (e.g. `deck.js`) is linkified
  // even when its extension isn't in the static artifact allowlist.
  const artifactFilenames = createMemo(() => collectArtifactFilenames(allParts()), [] as string[], {
    equals: sameStrings,
  })
  const attemptedFileWork = createMemo(() => turnAttemptedFileWork(allParts()))
  return (
    <Index each={visibleGrouped()}>
      {(entryAccessor) => {
        const entryType = createMemo(() => entryAccessor().type)

        return (
          <Switch>
            <Match when={entryType() === "context"}>
              {(() => {
                const parts = createMemo(
                  () => {
                    const entry = entryAccessor()
                    if (entry.type !== "context") return emptyTools
                    return entry.refs
                      .map((ref) => part().get(ref.messageID)?.get(ref.partID))
                      .filter((part): part is ToolPart => !!part && isContextGroupTool(part))
                  },
                  emptyTools,
                  { equals: same },
                )
                const busy = createMemo(() => props.working && last() === entryAccessor().key)

                return (
                  <Show when={parts().length > 0}>
                    <ContextToolGroup parts={parts()} busy={busy()} />
                  </Show>
                )
              })()}
            </Match>
            <Match when={entryType() === "tasks"}>
              {(() => {
                const parts = createMemo(
                  () => {
                    const entry = entryAccessor()
                    if (entry.type !== "tasks") return emptyTools
                    return entry.refs
                      .map((ref) => part().get(ref.messageID)?.get(ref.partID))
                      .filter((part): part is ToolPart => !!part && isTaskToolPart(part))
                  },
                  emptyTools,
                  { equals: same },
                )

                return (
                  <Show when={parts().length > 0}>
                    <TaskFleet parts={parts()} busy={props.working} />
                  </Show>
                )
              })()}
            </Match>
            <Match when={entryType() === "part"}>
              {(() => {
                const message = createMemo(() => {
                  const entry = entryAccessor()
                  if (entry.type !== "part") return
                  return msgs().get(entry.ref.messageID)
                })
                const item = createMemo(() => {
                  const entry = entryAccessor()
                  if (entry.type !== "part") return
                  return part().get(entry.ref.messageID)?.get(entry.ref.partID)
                })

                return (
                  <Show when={message()}>
                    <Show when={item()}>
                      <Part
                        part={item()!}
                        message={message()!}
                        showAssistantCopyPartID={props.showAssistantCopyPartID}
                        turnDurationMs={props.turnDurationMs}
                        suppressArtifactPreviews={props.suppressArtifactPreviews}
                        defaultOpen={partDefaultOpen(item()!, props.shellToolDefaultOpen, props.editToolDefaultOpen)}
                        artifactFilenames={artifactFilenames()}
                        attemptedFileWork={attemptedFileWork()}
                      />
                    </Show>
                  </Show>
                )
              })()}
            </Match>
          </Switch>
        )
      }}
    </Index>
  )
}

function isContextGroupTool(part: PartType): part is ToolPart {
  return part.type === "tool" && CONTEXT_GROUP_TOOLS.has(part.tool)
}

function contextToolDetail(part: ToolPart, i18n: ReturnType<typeof useI18n>): string | undefined {
  const info = getToolInfo(part.tool, part.state.input ?? {})
  if (info.subtitle) return info.subtitle
  if (part.state.status === "error") {
    const outcome = toolOutcome(partMetadata(part))
    return outcome ? i18n.t(toolOutcomeText(outcome, part.tool).body) : part.state.error
  }
  if ((part.state.status === "running" || part.state.status === "completed") && part.state.title)
    return part.state.title
  const description = part.state.input?.description
  if (typeof description === "string") return description
  return undefined
}

function contextToolIcon(part: ToolPart): IconProps["name"] {
  if (part.tool === "grep") return "magnifying-glass"
  if (part.tool === "read") return "file-glass"
  if (part.tool === "bash") return "terminal"
  if (part.tool === "list" || part.tool === "glob") return "file-glass"
  return "hammer"
}

function contextToolValue(value: unknown) {
  if (typeof value === "string") return value
  if (value === undefined || value === null) return ""
  return JSON.stringify(value)
}

function contextToolRequest(part: ToolPart) {
  return contextToolValue(part.state.input)
}

function contextToolResponse(part: ToolPart, i18n: ReturnType<typeof useI18n>) {
  if (part.state.status === "error") {
    const outcome = toolOutcome(partMetadata(part))
    return outcome ? i18n.t(toolOutcomeText(outcome, part.tool).body) : part.state.error
  }
  if (part.state.status !== "completed") return ""
  return contextToolValue(part.state.output)
}

function contextToolTrigger(part: ToolPart, i18n: ReturnType<typeof useI18n>) {
  const input = (part.state.input ?? {}) as Record<string, unknown>
  const path = typeof input.path === "string" ? input.path : "/"
  const filePath = typeof input.filePath === "string" ? input.filePath : undefined
  const pattern = typeof input.pattern === "string" ? input.pattern : undefined
  const include = typeof input.include === "string" ? input.include : undefined
  const offset = typeof input.offset === "number" ? input.offset : undefined
  const limit = typeof input.limit === "number" ? input.limit : undefined

  switch (part.tool) {
    case "read": {
      const args: string[] = []
      if (offset !== undefined) args.push("offset=" + offset)
      if (limit !== undefined) args.push("limit=" + limit)
      return {
        title: i18n.t("ui.tool.read"),
        subtitle: filePath ? getFilename(filePath) : "",
        args,
      }
    }
    case "list":
      return {
        title: i18n.t("ui.tool.list"),
        subtitle: getDirectory(path),
      }
    case "glob":
      return {
        title: i18n.t("ui.tool.glob"),
        subtitle: getDirectory(path),
        args: pattern ? ["pattern=" + pattern] : [],
      }
    case "grep": {
      const args: string[] = []
      if (pattern) args.push("pattern=" + pattern)
      if (include) args.push("include=" + include)
      return {
        title: i18n.t("ui.tool.grep"),
        subtitle: getDirectory(path),
        args,
      }
    }
    case "bash": {
      const description = input.description
      return {
        title: typeof description === "string" && description.length > 0 ? description : i18n.t("ui.tool.shell"),
        subtitle: "",
        args: [],
      }
    }
    default: {
      const info = getToolInfo(part.tool, input)
      return {
        title: info.title,
        subtitle: info.subtitle || contextToolDetail(part, i18n),
        args: [],
      }
    }
  }
}

function ExaOutput(props: { output?: string }) {
  const links = createMemo(() => urls(props.output))

  return (
    <Show when={links().length > 0}>
      <div data-component="exa-tool-output">
        <div data-slot="exa-tool-links">
          <For each={links()}>
            {(url) => (
              <a
                data-slot="exa-tool-link"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                {url}
              </a>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

export function registerPartComponent(type: string, component: PartComponent) {
  PART_MAPPING[type] = component
}

export function Message(props: MessageProps) {
  return (
    <Switch>
      <Match when={props.message.role === "user" && props.message}>
        {(userMessage) => (
          <UserMessageDisplay message={userMessage() as UserMessage} parts={props.parts} actions={props.actions} />
        )}
      </Match>
      <Match when={props.message.role === "assistant" && props.message}>
        {(assistantMessage) => (
          <AssistantMessageDisplay
            message={assistantMessage() as AssistantMessage}
            parts={props.parts}
            showAssistantCopyPartID={props.showAssistantCopyPartID}
            showReasoningSummaries={props.showReasoningSummaries}
          />
        )}
      </Match>
    </Switch>
  )
}

export function AssistantMessageDisplay(props: {
  message: AssistantMessage
  parts: PartType[]
  showAssistantCopyPartID?: string | null
  showReasoningSummaries?: boolean
}) {
  const emptyTools: ToolPart[] = []
  const part = createMemo(() => index(props.parts))
  const grouped = createMemo(
    () =>
      groupParts(
        props.parts
          .filter((part) => renderable(part, props.showReasoningSummaries ?? true))
          .map((part) => ({
            messageID: props.message.id,
            part,
          })),
      ),
    [] as PartGroup[],
    { equals: sameGroups },
  )
  // GigaTool: same artifact-filename linkification AssistantParts does. This
  // path only sees one message's parts, but the renderer (session-turn.tsx)
  // only ever hands Message a user message, so assistant text normally renders
  // through AssistantParts; wiring it here keeps this exported component
  // consistent for any other caller that renders an assistant message directly.
  const artifactFilenames = createMemo(() => collectArtifactFilenames(props.parts), [] as string[], {
    equals: sameStrings,
  })
  const attemptedFileWork = createMemo(() => turnAttemptedFileWork(props.parts))

  return (
    <Index each={grouped()}>
      {(entryAccessor) => {
        const entryType = createMemo(() => entryAccessor().type)

        return (
          <Switch>
            <Match when={entryType() === "context"}>
              {(() => {
                const parts = createMemo(
                  () => {
                    const entry = entryAccessor()
                    if (entry.type !== "context") return emptyTools
                    return entry.refs
                      .map((ref) => part().get(ref.partID))
                      .filter((part): part is ToolPart => !!part && isContextGroupTool(part))
                  },
                  emptyTools,
                  { equals: same },
                )

                return (
                  <Show when={parts().length > 0}>
                    <ContextToolGroup parts={parts()} />
                  </Show>
                )
              })()}
            </Match>
            <Match when={entryType() === "part"}>
              {(() => {
                const item = createMemo(() => {
                  const entry = entryAccessor()
                  if (entry.type !== "part") return
                  return part().get(entry.ref.partID)
                })

                return (
                  <Show when={item()}>
                    <Part
                      part={item()!}
                      message={props.message}
                      showAssistantCopyPartID={props.showAssistantCopyPartID}
                      artifactFilenames={artifactFilenames()}
                      attemptedFileWork={attemptedFileWork()}
                    />
                  </Show>
                )
              })()}
            </Match>
          </Switch>
        )
      }}
    </Index>
  )
}

function ContextToolGroup(props: { parts: ToolPart[]; busy?: boolean }) {
  const i18n = useI18n()
  const [open, setOpen] = createSignal(false)
  const [openRows, setOpenRows] = createSignal<Record<number, boolean>>({})
  // When a live busy signal is available (the interactive session view always
  // passes one - see AssistantParts above), trust it exclusively: a part left
  // "pending"/"running" in storage can be permanently stale (e.g. the app was
  // force-quit mid-tool-call, so nothing ever finalized it), and the session
  // being busy is the only signal that can't lie about "is this still
  // actually happening" after a restart. Only fall back to the parts' own
  // status when no busy signal is passed at all (e.g. a static/standalone
  // render with no live session context).
  const pending = createMemo(() =>
    props.busy !== undefined
      ? props.busy
      : props.parts.some((part) => part.state.status === "pending" || part.state.status === "running"),
  )
  const summary = createMemo(() => {
    const commandCount = props.parts.filter((part) => part.tool === "bash").length
    const toolCount = props.parts.length - commandCount
    if (commandCount && toolCount) return `Used ${toolCount} tools \u00B7 ran ${commandCount} command`
    if (commandCount) return `Ran ${commandCount} command`
    return `Used ${props.parts.length} tools`
  })
  const icon = createMemo<IconProps["name"]>(() => {
    const commandCount = props.parts.filter((part) => part.tool === "bash").length
    if (commandCount === props.parts.length) return "terminal"
    return "hammer"
  })

  return (
    <Collapsible
      data-slot="context-tool-group"
      open={open()}
      onOpenChange={setOpen}
      variant="ghost"
      class="tool-collapsible"
    >
      <Collapsible.Trigger>
        <div data-component="context-tool-group-trigger">
          <span data-slot="context-tool-group-icon">
            <Icon name={icon()} size="normal" />
          </span>
          <span data-slot="context-tool-group-title">
            <span data-slot="context-tool-group-label">
              <ToolStatusTitle active={pending()} activeText={summary()} doneText={summary()} split={false} />
            </span>
          </span>
          <Collapsible.Arrow />
        </div>
      </Collapsible.Trigger>
      <Collapsible.Content data-activity-row-body="">
        <div data-component="context-tool-group-list">
          <Index each={props.parts}>
            {(partAccessor) => {
              const trigger = createMemo(() => contextToolTrigger(partAccessor(), i18n))
              // Same reasoning as `pending` above: a busy signal, when passed,
              // is authoritative - a row otherwise stuck "running" forever in
              // storage must not shimmer once the turn itself is known idle.
              const running = createMemo(() => {
                const raw = partAccessor().state.status === "pending" || partAccessor().state.status === "running"
                return props.busy !== undefined ? props.busy && raw : raw
              })
              const rowIndex = () => props.parts.indexOf(partAccessor())
              const request = createMemo(() => contextToolRequest(partAccessor()))
              const response = createMemo(() => contextToolResponse(partAccessor(), i18n))
              const hasDetail = createMemo(() => !!request() || !!response())
              const rowOpen = createMemo(() => !!openRows()[rowIndex()])
              const toggleRow = () => {
                if (!hasDetail()) return
                setOpenRows({ ...openRows(), [rowIndex()]: !rowOpen() })
              }

              return (
                <div data-slot="context-tool-group-item">
                  <div data-slot="context-tool-group-rail">
                    <Icon name={contextToolIcon(partAccessor())} size="normal" />
                  </div>
                  <div data-slot="context-tool-group-row-content">
                    <button
                      type="button"
                      data-slot="context-tool-group-row-head"
                      data-interactive={hasDetail() ? "" : undefined}
                      disabled={!hasDetail()}
                      onClick={toggleRow}
                    >
                      <span data-slot="context-tool-group-row-label">
                        <TextShimmer text={trigger().title} active={running()} />
                      </span>
                      <Show when={!running() && trigger().subtitle}>
                        <span data-slot="context-tool-group-row-subtitle">{trigger().subtitle}</span>
                      </Show>
                      <Show when={!running() && trigger().args?.length}>
                        <For each={trigger().args}>
                          {(arg) => <span data-slot="context-tool-group-row-arg">{arg}</span>}
                        </For>
                      </Show>
                      <Show when={autoApprovalOf(partMetadata(partAccessor()))}>
                        {(approval) => <AutoApprovedMark approval={approval()} />}
                      </Show>
                      <Show when={hasDetail()}>
                        <Icon
                          name="chevron-right"
                          size="normal"
                          class="context-tool-group-row-chevron"
                          classList={{ rotated: rowOpen() }}
                        />
                      </Show>
                    </button>
                    <Show when={rowOpen() && hasDetail()}>
                      <div data-slot="context-tool-group-detail">
                        <Show when={request()}>
                          {(value) => (
                            <div data-slot="context-tool-group-section">
                              <div data-slot="context-tool-group-section-label">Request</div>
                              <pre>{value()}</pre>
                            </div>
                          )}
                        </Show>
                        <Show when={response()}>
                          {(value) => (
                            <div data-slot="context-tool-group-section">
                              <div data-slot="context-tool-group-section-label">Response</div>
                              <pre>{value()}</pre>
                            </div>
                          )}
                        </Show>
                      </div>
                    </Show>
                  </div>
                </div>
              )
            }}
          </Index>
        </div>
      </Collapsible.Content>
    </Collapsible>
  )
}

// A run of parallel subagent (task) calls rendered as one "fleet" widget:
// a collapsed chip with the agent count, live cumulative token spend, elapsed
// time and one progress dot per agent; expanding it reveals a per-agent table
// whose rows navigate into the child sessions. Live data comes from the same
// stores the rest of the feed uses: child sessions arrive via session.created
// events (parentID set), their spend accumulates via message.updated, and
// busy/idle comes from session_status.
function TaskFleet(props: { parts: ToolPart[]; busy?: boolean }) {
  const data = useData()
  const i18n = useI18n()
  const location = useLocation()
  const [open, setOpen] = createSignal(false)
  const [now, setNow] = createSignal(Date.now())

  // Per-row fine-grained state: indexArray keeps one row scope per part, so a
  // token update on one child recomputes only that row's spend memo instead of
  // rebuilding every row (during a fan-out these fire many times a second).
  const rows = indexArray(
    () => props.parts,
    (part) => {
      const input = createMemo(() => (part().state.input ?? {}) as Record<string, any>)
      const metadata = createMemo(() => {
        const s = part().state
        return ("metadata" in s ? (s.metadata ?? {}) : {}) as Record<string, any>
      })
      const background = createMemo(() => metadata().background === true)
      const childId = createMemo(() => {
        const meta = metadata()
        if (typeof meta.sessionId === "string" && meta.sessionId) return meta.sessionId as string
        // The title-matching fallback scans every session — never run it for a
        // part that died before metadata landed, or the whole fleet would stay
        // subscribed to every session title forever.
        if (part().state.status === "error") return undefined
        return taskSession(input(), location.pathname, data.store.session, data.store.agent)
      })
      const child = createMemo(() => {
        const id = childId()
        if (!id) return undefined
        const sessions = data.store.session
        const at = Binary.search(sessions, id, (session) => session.id)
        return at.found ? sessions[at.index] : undefined
      })
      const agent = createMemo(() => taskAgent(input().subagent_type, data.store.agent))
      const childBusy = createMemo(() => {
        const id = childId()
        if (!id) return false
        const status = data.store.session_status?.[id]?.type
        return status === "busy" || status === "retry"
      })
      // For a background part the tool call completes instantly while the
      // child keeps working — the child's live status is the truth there
      // (a child left "busy" in a dead status map can't lie: it is live-only).
      // For foreground parts the part status alone is NOT enough: a part left
      // "running" by a force-quit turn would spin (and tick a timer) forever,
      // so the turn's busy signal gates it — same hazard ContextToolGroup
      // documents for its pending states.
      const state = createMemo<"running" | "done" | "error">(() => {
        const status = part().state.status
        if (status === "error") return "error"
        if (background()) return childBusy() ? "running" : "done"
        if (status === "completed") return "done"
        return props.busy !== undefined && !props.busy ? "done" : "running"
      })
      const start = createMemo(() => {
        const s = part().state
        const partStart = "time" in s ? s.time?.start : undefined
        return (background() ? child()?.time.created : undefined) ?? partStart ?? child()?.time.created
      })
      const end = createMemo(() => {
        if (state() === "running") return undefined
        const s = part().state
        const partEnd = s.status === "completed" || s.status === "error" ? s.time?.end : undefined
        return (background() ? child()?.time.updated : undefined) ?? partEnd ?? child()?.time.updated
      })
      const tokens = createMemo(() => {
        const id = childId()
        return sessionSpend(id ? data.store.message?.[id] : undefined).tokens
      })
      const description = createMemo(() => {
        const value = input().description
        return typeof value === "string" ? value : ""
      })
      return { description, agent, childId, background, state, start, end, tokens }
    },
  )

  const anyRunning = createMemo(() => rows().some((row) => row.state() === "running"))

  createEffect(
    on(anyRunning, (running) => {
      if (!running) return
      setNow(Date.now())
      const timer = setInterval(() => setNow(Date.now()), 1000)
      onCleanup(() => clearInterval(timer))
    }),
  )

  const rowElapsed = (row: ReturnType<typeof rows>[number]) => {
    const start = row.start()
    if (typeof start !== "number") return ""
    const end = row.state() === "running" ? now() : (row.end() ?? start)
    return formatElapsed(Math.max(0, end - start))
  }

  const totalTokens = createMemo(() => rows().reduce((sum, row) => sum + row.tokens(), 0))
  const headerElapsed = createMemo(() => {
    const list = rows().filter((row) => typeof row.start() === "number")
    if (list.length === 0) return ""
    const current = now()
    const longest = Math.max(
      ...list.map((row) => {
        const start = row.start()!
        const end = row.state() === "running" ? current : (row.end() ?? start)
        return Math.max(0, end - start)
      }),
    )
    return formatElapsed(longest)
  })

  const summary = createMemo(() => {
    const parts = [i18n.t("ui.taskFleet.agents", { count: rows().length })]
    const tokens = totalTokens()
    if (tokens > 0) parts.push(i18n.t("ui.taskFleet.tokens", { count: formatTokenCount(tokens) }))
    const elapsed = headerElapsed()
    if (elapsed) parts.push(elapsed)
    return parts.join(" · ")
  })

  const openRow = (childId: string | undefined) => {
    if (!childId) return
    if (data.navigateToSession) {
      data.navigateToSession(childId)
      return
    }
    const href = sessionLink(childId, location.pathname, data.sessionHref)
    if (href) window.location.assign(href)
  }

  return (
    <Collapsible data-slot="task-fleet" open={open()} onOpenChange={setOpen} variant="ghost" class="tool-collapsible">
      <Collapsible.Trigger>
        <div data-component="task-fleet-trigger">
          <span data-slot="task-fleet-icon">
            <Icon name="robot" size="normal" />
          </span>
          <span data-slot="task-fleet-title">
            <ToolStatusTitle active={anyRunning()} activeText={summary()} doneText={summary()} split={false} />
          </span>
          <span data-slot="task-fleet-dots" aria-hidden="true">
            <For each={rows()}>
              {(row) => <span data-slot="task-fleet-dot" data-state={row.state()} style={{ color: row.agent().color }} />}
            </For>
          </span>
          <Collapsible.Arrow />
        </div>
      </Collapsible.Trigger>
      <Collapsible.Content data-activity-row-body="">
        <div data-component="task-fleet-list">
          <For each={rows()}>
            {(row) => (
              <button
                type="button"
                data-slot="task-fleet-row"
                data-interactive={row.childId() ? "" : undefined}
                disabled={!row.childId()}
                onClick={() => openRow(row.childId())}
              >
                <span data-slot="task-fleet-row-status" data-state={row.state()} style={{ color: row.agent().color }}>
                  <Switch>
                    <Match when={row.state() === "done"}>
                      <Icon name="circle-check" size="small" />
                    </Match>
                    <Match when={row.state() === "error"}>
                      <Icon name="circle-x" size="small" />
                    </Match>
                    <Match when={true}>
                      <span data-slot="task-fleet-row-spinner" />
                    </Match>
                  </Switch>
                </span>
                <span data-slot="task-fleet-row-agent" style={{ color: row.agent().color }}>
                  {row.agent().name ?? i18n.t("ui.tool.agent.default")}
                </span>
                <span data-slot="task-fleet-row-description">
                  <TextShimmer text={row.description()} active={row.state() === "running"} />
                </span>
                <Show when={row.background()}>
                  <span data-slot="task-fleet-row-badge">{i18n.t("ui.taskFleet.background")}</span>
                </Show>
                <span data-slot="task-fleet-row-metrics">
                  <Show when={row.tokens() > 0}>
                    <span data-slot="task-fleet-row-tokens">{formatTokenCount(row.tokens())}</span>
                  </Show>
                  <span data-slot="task-fleet-row-time">{rowElapsed(row)}</span>
                </span>
              </button>
            )}
          </For>
        </div>
      </Collapsible.Content>
    </Collapsible>
  )
}

export function UserMessageDisplay(props: { message: UserMessage; parts: PartType[]; actions?: UserActions }) {
  const dialog = useDialog()
  const i18n = useI18n()
  const [state, setState] = createStore({
    copied: false,
    busy: false,
  })
  const copied = () => state.copied
  const busy = () => state.busy
  const now = useCalendarNow()

  const textPart = createMemo(
    () => props.parts?.find((p) => p.type === "text" && !(p as TextPart).synthetic) as TextPart | undefined,
  )

  const text = createMemo(() => textPart()?.text || "")

  const files = createMemo(() => (props.parts?.filter((p) => p.type === "file") as FilePart[]) ?? [])

  const attachments = createMemo(() => files().filter(attached))

  const inlineFiles = createMemo(() => files().filter(inline))

  const agents = createMemo(() => (props.parts?.filter((p) => p.type === "agent") as AgentPart[]) ?? [])

  // A bare clock reading is only self-explanatory on the day it was written —
  // "16:59" in a chat from last month says nothing. So the label carries just
  // enough to place the message: the date appears once it isn't today, and the
  // year only once it differs. (This row is revealed on message hover, so the
  // longer form costs nothing at rest.) The exact stamp lives in the tooltip.
  const timefmt = createMemo(() => new Intl.DateTimeFormat(i18n.locale(), { hour: "numeric", minute: "2-digit" }))
  const datefmt = createMemo(
    () =>
      new Intl.DateTimeFormat(i18n.locale(), { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }),
  )
  const datefmtWithYear = createMemo(
    () =>
      new Intl.DateTimeFormat(i18n.locale(), {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
  )
  const fullfmt = createMemo(() => new Intl.DateTimeFormat(i18n.locale(), { dateStyle: "full", timeStyle: "short" }))

  const stamp = createMemo(() => {
    const created = props.message.time?.created
    if (typeof created !== "number") return ""
    const at = new Date(created)
    const current = now()
    if (at.toDateString() === current.toDateString()) return timefmt().format(at)
    if (at.getFullYear() === current.getFullYear()) return datefmt().format(at)
    return datefmtWithYear().format(at)
  })

  const stampFull = createMemo(() => {
    const created = props.message.time?.created
    if (typeof created !== "number") return ""
    return fullfmt().format(created)
  })

  const metaTail = stamp

  const openImagePreview = (url: string, alt?: string) => {
    dialog.show(() => <ImagePreview src={url} alt={alt} />)
  }

  const downloadAttachment = (url: string, filename: string) => {
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  // Decode a data: URL (base64 or percent-encoded) into text, for previewing
  // text attachments (e.g. audio transcript .txt) without downloading.
  const decodeDataUrlText = (url: string) => {
    const idx = url.indexOf(",")
    if (idx === -1) return ""
    const meta = url.slice(0, idx)
    const data = url.slice(idx + 1)
    try {
      if (meta.includes(";base64")) {
        const binary = atob(data)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        return new TextDecoder().decode(bytes)
      }
      return decodeURIComponent(data)
    } catch {
      return ""
    }
  }

  const isTextAttachment = (file: FilePart) => file.mime?.startsWith("text/") ?? false

  const openTextPreview = (url: string, filename: string) => {
    const content = decodeDataUrlText(url)
    dialog.show(() => (
      <TextPreview text={content} filename={filename} onDownload={() => downloadAttachment(url, filename)} />
    ))
  }

  const handleCopy = async () => {
    const content = text()
    if (!content) return
    await navigator.clipboard.writeText(content)
    setState("copied", true)
    setTimeout(() => setState("copied", false), 2000)
  }

  const revert = () => {
    const act = props.actions?.revert
    if (!act || busy()) return
    setState("busy", true)
    void Promise.resolve()
      .then(() =>
        act({
          sessionID: props.message.sessionID,
          messageID: props.message.id,
        }),
      )
      .finally(() => setState("busy", false))
  }

  return (
    <div data-component="user-message">
      <Show when={attachments().length > 0}>
        <div data-slot="user-message-attachments">
          <For each={attachments()}>
            {(file) => {
              const type = kind(file)
              const name = file.filename ?? i18n.t("ui.message.attachment.alt")
              // Share-scrubbed attachment: content was never published, so the
              // chip is inert — no preview, no download, explanatory tooltip.
              if (stripped(file)) {
                return (
                  <Tooltip
                    value={`${name} — ${i18n.t("ui.message.attachment.notPublished")}`}
                    placement="top"
                    openDelay={250}
                    contentClass="max-w-[min(90vw,520px)] break-all"
                    class="w-[min(220px,100%)] min-w-0"
                  >
                    <div data-slot="user-message-attachment" data-type="file" class="w-full! opacity-60">
                      <div data-slot="user-message-attachment-file">
                        <FileIcon node={{ path: name, type: "file" }} />
                        <span data-slot="user-message-attachment-name">{name}</span>
                      </div>
                    </div>
                  </Tooltip>
                )
              }

              // Reveal-in-Finder + context menu for any on-disk upload that has a
              // resolvable local path — mime-agnostic on purpose: .json/.md/.txt
              // are all normalized to text/plain on attach (attachmentMime), so
              // gating on the mime previously left them with no menu at all (the
              // reported bug). Images keep their in-app preview regardless. A
              // text-mime attachment WITHOUT a path — a synthetic CSV/transcript
              // pill, which never gets a localPath — has no href here and falls
              // back to the in-app text preview below, unaffected by this change.
              const href = type === "image" ? undefined : attachmentFileHref(file)
              return (
                // Custom tooltip (not the native `title`): Chromium suppresses
                // the native tooltip on the 2nd of two adjacent chips within one
                // hover, and its open delay is fixed/slow. A short openDelay here
                // shows the full name reliably and quickly for every chip.
                <Tooltip
                  value={name}
                  placement="top"
                  openDelay={250}
                  contentClass="max-w-[min(90vw,520px)] break-all"
                  // The tooltip trigger is now the flex item in the attachments
                  // row, so it must carry the chip's box; the chip then fills it.
                  class={type === "image" ? "size-12 shrink-0" : "w-[min(220px,100%)] min-w-0"}
                >
                  <Dynamic
                    component={href ? "a" : "div"}
                    data-slot="user-message-attachment"
                    data-type={type}
                    data-clickable="true"
                    class={href ? "external-link w-full!" : "w-full!"}
                    // Anchor: the desktop global click delegate handles file://
                    // (reveal) and the OS context menu is offered automatically.
                    {...(href
                      ? { href, target: "_blank", rel: "noopener noreferrer" }
                      : {
                          onClick: () => {
                            if (type === "image") openImagePreview(file.url, name)
                            else if (isTextAttachment(file)) openTextPreview(file.url, name)
                            else downloadAttachment(file.url, name)
                          },
                        })}
                  >
                    <Show
                      when={type === "image"}
                      fallback={
                        <div data-slot="user-message-attachment-file">
                          <FileIcon node={{ path: name, type: "file" }} />
                          <span data-slot="user-message-attachment-name">{name}</span>
                        </div>
                      }
                    >
                      <img data-slot="user-message-attachment-image" src={file.url} alt={name} />
                    </Show>
                  </Dynamic>
                </Tooltip>
              )
            }}
          </For>
        </div>
      </Show>
      <Show when={text()}>
        <>
          <div data-slot="user-message-body">
            <div data-slot="user-message-text">
              <HighlightedText text={text()} references={inlineFiles()} agents={agents()} />
            </div>
          </div>
          <div data-slot="user-message-copy-wrapper">
            <Show when={metaTail()}>
              {/* A div, not a span: Tooltip's trigger is a div, and phrasing
                  content can't hold flow content. */}
              <div data-slot="user-message-meta-wrap">
                <Tooltip value={stampFull()} placement="top" class="shrink-0" tabIndex={0}>
                  <span data-slot="user-message-meta-tail" class="text-12-regular text-text-weak cursor-help">
                    {metaTail()}
                  </span>
                </Tooltip>
              </div>
            </Show>
            <Show when={props.actions?.revert}>
              <Tooltip value={i18n.t("ui.message.revertMessage")} placement="top">
                <IconButton
                  icon="arrow-left"
                  size="normal"
                  variant="ghost"
                  disabled={!!busy()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation()
                    revert()
                  }}
                  aria-label={i18n.t("ui.message.revertMessage")}
                />
              </Tooltip>
            </Show>
            <Tooltip value={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copyMessage")} placement="top">
              <IconButton
                icon={copied() ? "check" : "copy"}
                size="normal"
                variant="ghost"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation()
                  void handleCopy()
                }}
                aria-label={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copyMessage")}
              />
            </Tooltip>
          </div>
        </>
      </Show>
    </div>
  )
}

type HighlightSegment = { text: string; type?: "file" | "agent" | "chat" }

// GigaTool: a pasted chat deep link (gigatool://open-session?…) in a user
// message is an opaque wall of URL text — split it out into a "chat" segment
// rendered as a labelled link instead.
function splitChatLinkSegments(segments: HighlightSegment[]) {
  const out: HighlightSegment[] = []
  for (const segment of segments) {
    if (segment.type) {
      out.push(segment)
      continue
    }
    const text = segment.text
    let cursor = 0
    for (const match of text.matchAll(chatSessionLinkPattern)) {
      const href = match[0].replace(/[),.;!?]+$/, "")
      if (match.index > cursor) out.push({ text: text.slice(cursor, match.index) })
      out.push({ text: href, type: "chat" })
      cursor = match.index + href.length
    }
    if (cursor < text.length) out.push({ text: text.slice(cursor) })
  }
  return out
}

function HighlightedText(props: { text: string; references: FilePart[]; agents: AgentPart[] }) {
  const segments = createMemo(() => {
    const text = props.text

    const allRefs: { start: number; end: number; type: "file" | "agent" }[] = [
      ...props.references
        .filter((r) => r.source?.text?.start !== undefined && r.source?.text?.end !== undefined)
        .map((r) => ({ start: r.source!.text!.start, end: r.source!.text!.end, type: "file" as const })),
      ...props.agents
        .filter((a) => a.source?.start !== undefined && a.source?.end !== undefined)
        .map((a) => ({ start: a.source!.start, end: a.source!.end, type: "agent" as const })),
    ].sort((a, b) => a.start - b.start)

    const result: HighlightSegment[] = []
    let lastIndex = 0

    for (const ref of allRefs) {
      if (ref.start < lastIndex) continue

      if (ref.start > lastIndex) {
        result.push({ text: text.slice(lastIndex, ref.start) })
      }

      result.push({ text: text.slice(ref.start, ref.end), type: ref.type })
      lastIndex = ref.end
    }

    if (lastIndex < text.length) {
      result.push({ text: text.slice(lastIndex) })
    }

    return splitChatLinkSegments(result)
  })

  const i18n = useI18n()
  const resolveChatTitle = useChatLinkTitles()

  return (
    <For each={segments()}>
      {(segment) =>
        segment.type === "chat" ? (
          <a data-highlight="chat" class="external-link" href={segment.text} target="_blank" rel="noopener noreferrer">
            {chatSessionLinkText(segment.text, i18n.t("ui.message.chatLink"), resolveChatTitle)}
          </a>
        ) : (
          <span data-highlight={segment.type}>{segment.text}</span>
        )
      }
    </For>
  )
}

// Spread, not a hand-written list: this is the ONLY path from a message part to
// its renderer, and a prop left out of the list fails silently — the renderer
// reads `undefined` and simply never shows what it was for. `attemptedFileWork`
// was missing here from the day it was added, so the missing-file warning it
// gates (feedback #139: the build threw, the model said «Файл создан», and the
// only signal was a chip that never appeared) could not render in a real
// session at all, in any build. Nothing failed, nothing was logged, and the two
// callers that compute the value looked correct. Spreading keeps the seam from
// needing a checklist: every field of MessagePartProps reaches the part
// component, and a new one does so by default rather than by remembering.
//
// `component` LAST, after the spread: mergeProps gives later sources precedence,
// so a `component` key on MessagePartProps would otherwise take over Dynamic's
// own control prop — and Dynamic would then try to render that value, fall
// through its typeof switch, and blank every message part in the chat. There is
// no such key today and typecheck would not object to one (DynamicProps accepts
// `string & {}`), which is exactly why the order is the guard.
export function Part(props: MessagePartProps) {
  const component = createMemo(() => PART_MAPPING[props.part.type])
  return (
    <Show when={component()}>
      <Dynamic {...props} component={component()} />
    </Show>
  )
}

export interface ToolProps {
  input: Record<string, any>
  metadata: Record<string, any>
  tool: string
  title?: string
  output?: string
  status?: string
  hideDetails?: boolean
  defaultOpen?: boolean
  forceOpen?: boolean
  locked?: boolean
}

export type ToolComponent = Component<ToolProps>

const state: Record<
  string,
  {
    name: string
    render?: ToolComponent
  }
> = {}

export function registerTool(input: { name: string; render?: ToolComponent }) {
  state[input.name] = input
  return input
}

export function getTool(name: string) {
  return state[name]?.render
}

export const ToolRegistry = {
  register: registerTool,
  render: getTool,
}

function toolError(error: string) {
  return error.replace(/^Error:\s*/, "").trim()
}

function ToolError(props: { tool: string; input: Record<string, any>; error: string }) {
  const info = getToolInfo(props.tool, props.input)
  const parsed = createMemo(() => toolError(props.error))
  const i18n = useI18n()
  const title = createMemo(() => {
    if (props.tool === "skill") return i18n.t("ui.tool.skill.error")
    return i18n.t("ui.tool.error.title", { tool: info.title })
  })

  return (
    <BasicTool
      icon={info.icon}
      trigger={{
        title: title(),
      }}
    >
      <div data-component="tool-error-output">{parsed()}</div>
    </BasicTool>
  )
}

// A call that ended by a decision — the user's rejection, or a stop — rather
// than by failing (tool-outcome.ts). Neutral on purpose: no «Ошибка», and the
// body in the weak text colour, because nothing went wrong.
function ToolOutcomeNotice(props: { tool: string; input: Record<string, any>; outcome: ToolOutcome }) {
  const info = getToolInfo(props.tool, props.input)
  const i18n = useI18n()
  const text = createMemo(() => toolOutcomeText(props.outcome, props.tool))
  const feedback = () => (props.outcome.kind === "rejected" ? props.outcome.feedback : undefined)

  return (
    <BasicTool
      icon={info.icon}
      trigger={{
        title: i18n.t(text().title),
        subtitle: info.subtitle,
      }}
    >
      <div data-component="tool-error-output" data-outcome={props.outcome.kind}>
        {i18n.t(text().body)}
        <Show when={feedback()}>
          {(value) => (
            <>
              {"\n"}
              {i18n.t("ui.tool.outcome.rejected.feedback", { feedback: value() })}
            </>
          )}
        </Show>
      </div>
    </BasicTool>
  )
}

function ToolFileAccordion(props: { path: string; actions?: JSX.Element; children: JSX.Element }) {
  const value = createMemo(() => props.path || "tool-file")

  return (
    <Accordion
      multiple
      data-scope="apply-patch"
      style={{ "--sticky-accordion-offset": "calc(32px + var(--tool-content-gap))" }}
      defaultValue={[value()]}
    >
      <Accordion.Item value={value()}>
        <StickyAccordionHeader>
          <Accordion.Trigger>
            <div data-slot="apply-patch-trigger-content">
              <div data-slot="apply-patch-file-info">
                <FileIcon node={{ path: props.path, type: "file" }} />
                <div data-slot="apply-patch-file-name-container">
                  <Show when={props.path.includes("/")}>
                    <span data-slot="apply-patch-directory">{`\u202A${getDirectory(props.path)}\u202C`}</span>
                  </Show>
                  <span data-slot="apply-patch-filename">{getFilename(props.path)}</span>
                </div>
              </div>
              <div data-slot="apply-patch-trigger-actions">
                {props.actions}
                <Icon name="chevron-grabber-vertical" size="small" />
              </div>
            </div>
          </Accordion.Trigger>
        </StickyAccordionHeader>
        <Accordion.Content>{props.children}</Accordion.Content>
      </Accordion.Item>
    </Accordion>
  )
}

function ToolDetails(props: { rows: Array<{ label: string; value?: string }> }) {
  const rows = createMemo(() => props.rows.filter((row): row is { label: string; value: string } => !!row.value))
  return (
    <Show when={rows().length}>
      <dl data-component="tool-details">
        <For each={rows()}>
          {(row) => (
            <div data-slot="tool-detail-row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          )}
        </For>
      </dl>
    </Show>
  )
}

PART_MAPPING["tool"] = function ToolPartDisplay(props) {
  const i18n = useI18n()
  const part = () => props.part as ToolPart
  if (part().tool === "todowrite") return null

  const hideQuestion = createMemo(
    () => part().tool === "question" && (part().state.status === "pending" || part().state.status === "running"),
  )

  const emptyInput: Record<string, any> = {}
  const emptyMetadata: Record<string, any> = {}

  const input = () => part().state?.input ?? emptyInput
  // @ts-expect-error
  const partMetadata = () => part().state?.metadata ?? emptyMetadata
  const partTitle = () => {
    const state = part().state
    if (!("title" in state) || typeof state.title !== "string") return undefined
    return state.title
  }

  const render = createMemo(() => ToolRegistry.render(part().tool) ?? GenericTool)

  return (
    <Show when={!hideQuestion()}>
      <div data-component="tool-part-wrapper">
        <Switch>
          <Match when={part().state.status === "error" && (part().state as any).error}>
            {(error) => {
              const cleaned = toolError(error())
              if (part().tool === "question" && cleaned.includes("dismissed this question")) {
                return (
                  <div style="width: 100%; display: flex; justify-content: flex-end;">
                    <span class="text-13-regular text-text-weak cursor-default">
                      {i18n.t("ui.messagePart.questions.dismissed")}
                    </span>
                  </div>
                )
              }
              const outcome = toolOutcome(partMetadata())
              if (outcome) return <ToolOutcomeNotice tool={part().tool} input={input()} outcome={outcome} />
              return <ToolError tool={part().tool} error={error()} input={input()} />
            }}
          </Match>
          <Match when={true}>
            <Dynamic
              component={render()}
              input={input()}
              tool={part().tool}
              title={partTitle()}
              metadata={partMetadata()}
              // @ts-expect-error
              output={part().state.output}
              status={part().state.status}
              hideDetails={props.hideDetails}
              defaultOpen={props.defaultOpen}
            />
          </Match>
        </Switch>
      </div>
    </Show>
  )
}

export function MessageDivider(props: { label: string }) {
  return (
    <div data-component="compaction-part">
      <div data-slot="compaction-part-divider">
        <span data-slot="compaction-part-line" />
        <span data-slot="compaction-part-label" class="text-12-regular text-text-weak">
          {props.label}
        </span>
        <span data-slot="compaction-part-line" />
      </div>
    </div>
  )
}

PART_MAPPING["compaction"] = function CompactionPartDisplay() {
  const i18n = useI18n()
  return <MessageDivider label={i18n.t("ui.messagePart.compaction")} />
}

PART_MAPPING["text"] = function TextPartDisplay(props) {
  const data = useData()
  const i18n = useI18n()
  const numfmt = createMemo(() => new Intl.NumberFormat(i18n.locale()))
  const part = () => props.part as TextPart
  const interrupted = createMemo(
    () =>
      props.message.role === "assistant" && (props.message as AssistantMessage).error?.name === "MessageAbortedError",
  )
  // Plan-mode messages don't create files — suppress both the chip list
  // (artifactPaths-derived ArtifactPreviews) and the inline-code filename
  // anchoring (linkArtifactFilenames in markdown.tsx). Any path the
  // assistant mentions in a plan is hypothetical, so a click affordance
  // would resolve to "file not found" and feel broken.
  const isPlanMessage = createMemo(() => (props.message as AssistantMessage).agent === "plan")

  const model = createMemo(() => {
    if (props.message.role !== "assistant") return ""
    const message = props.message as AssistantMessage
    const match = data.store.provider?.all?.find((p) => p.id === message.providerID)
    return match?.models?.[message.modelID]?.name ?? message.modelID
  })

  const duration = createMemo(() => {
    if (props.message.role !== "assistant") return ""
    const message = props.message as AssistantMessage
    const completed = message.time.completed
    const ms =
      typeof props.turnDurationMs === "number"
        ? props.turnDurationMs
        : typeof completed === "number"
          ? completed - message.time.created
          : -1
    if (!(ms >= 0)) return ""
    // Whole seconds here (unlike the live turn label, which counts up and wants
    // the decimal): this renders a finished duration.
    return formatDurationLabel(Math.round(ms / 1000) * 1000, (key, params) => i18n.t(key, params), numfmt())
  })

  const meta = createMemo(() => {
    if (props.message.role !== "assistant") return ""
    const items = [model(), duration(), interrupted() ? i18n.t("ui.message.interrupted") : ""]
    return items.filter((x) => !!x).join(" \u00B7 ")
  })

  const streaming = createMemo(
    () => props.message.role === "assistant" && typeof (props.message as AssistantMessage).time.completed !== "number",
  )
  const text = () => (part().text ?? "").trim()
  const isLastTextPart = createMemo(() => {
    const last = (data.store.part?.[props.message.id] ?? [])
      .filter((item): item is TextPart => item?.type === "text" && !!item.text?.trim())
      .at(-1)
    return last?.id === part().id
  })
  const showCopy = createMemo(() => {
    if (props.message.role !== "assistant") return isLastTextPart()
    if (props.showAssistantCopyPartID === null) return false
    if (typeof props.showAssistantCopyPartID === "string") return props.showAssistantCopyPartID === part().id
    return isLastTextPart()
  })
  const [copied, setCopied] = createSignal(false)

  const handleCopy = async () => {
    const content = text()
    if (!content) return
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // GigaTool: surface an artifact-preview chip below the text body for any
  // absolute path mentioned in the message whose extension we can render
  // (pdf/xlsx/pptx/docx/csv/md/html/json/py). The path is already
  // linkified by markdown.tsx::linkLocalPaths and clicking the link
  // reveals it in Finder; the chip adds the same expand-to-preview
  // affordance the tool-write artifact card has, for files the AI built
  // via shell command rather than the `write` tool.
  //
  // We don't reuse localPathRanges here because it greedily captures the
  // whole tail of the paragraph (used to keep visual link integrity in
  // the rendered text) — we want JUST the file portion, stopping at the
  // first whitespace/quote. The regex below matches /Users|/Volumes etc
  // followed by non-space chars ending in a previewable extension.
  const ArtifactPreview = useArtifactPreview()
  // Two patterns: absolute paths anywhere in text (most reliable — full
  // path means we can preview + reveal in Finder directly), and bare
  // filenames inside inline-code backticks (the AI's natural shorthand
  // when summarising "файл готов: `foo.xlsx`"). For bare filenames the
  // artifact-preview-card resolves the basename to a real path via the
  // SDK file-search endpoint before loading content.
  const artifactPaths = createMemo(() => collectArtifactPathsFromText(text()))
  // The host's extra action beside copy (context/message-actions.tsx), given
  // the answer's files as absolute paths: inline-code names are resolved by
  // the host's artifact resolver, and a name it cannot place is left out.
  const ExtraAction = useMessageExtraAction()
  const resolveArtifact = useArtifactPaths()
  const answerPaths = () =>
    artifactPaths().flatMap((p) => {
      if (p.startsWith("/") || /^[A-Za-z]:[\\/]/.test(p)) return [p]
      const resolved = resolveArtifact?.(p)
      return resolved ? [resolved] : []
    })

  return (
    <Show when={text()}>
      <div data-component="text-part">
        <div data-slot="text-part-body">
          <Show
            when={streaming()}
            fallback={
              <Markdown
                text={text()}
                cacheKey={part().id}
                streaming={false}
                linkArtifacts={!isPlanMessage()}
                artifactFilenames={props.artifactFilenames}
              />
            }
          >
            <PacedMarkdown
              text={text()}
              cacheKey={part().id}
              streaming={streaming()}
              linkArtifacts={!isPlanMessage()}
              artifactFilenames={props.artifactFilenames}
            />
          </Show>
          {/* See isPlanMessage comment above — same reason we suppress the
              chips here as we suppress the inline-code anchors. The outer
              Show keeps the function-callback form (so `component` is
              narrowed to the registered component, not `boolean`). */}
          <Show when={ArtifactPreview}>
            {(component) => (
              <Show when={!isPlanMessage() && !props.suppressArtifactPreviews}>
                <For each={artifactPaths()}>
                  {(path) => (
                    <Dynamic component={component()} path={path} attemptedFileWork={props.attemptedFileWork} />
                  )}
                </For>
              </Show>
            )}
          </Show>
        </div>
        <Show when={showCopy()}>
          <div data-slot="text-part-copy-wrapper" data-interrupted={interrupted() ? "" : undefined}>
            <Show when={props.message.role === "assistant"}>
              <Tooltip value={i18n.t("ui.message.forkMessage")} placement="top">
                <IconButton
                  icon="new-session"
                  size="normal"
                  variant="ghost"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation()
                    // Decoupled from props (Part renders via two deep paths); a
                    // session-level listener (session.tsx) forks from this turn.
                    window.dispatchEvent(
                      new CustomEvent("gigatool-fork-from-response", {
                        detail: { sessionID: props.message.sessionID, messageID: props.message.id },
                      }),
                    )
                  }}
                  aria-label={i18n.t("ui.message.forkMessage")}
                />
              </Tooltip>
            </Show>
            <Show when={props.message.role === "assistant" && ExtraAction}>
              {(extra) => (
                <Dynamic
                  component={extra()}
                  sessionID={props.message.sessionID}
                  messageID={props.message.id}
                  text={text()}
                  paths={answerPaths()}
                />
              )}
            </Show>
            <Tooltip value={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copyResponse")} placement="top">
              <IconButton
                icon={copied() ? "check" : "copy"}
                size="normal"
                variant="ghost"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCopy}
                aria-label={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copyResponse")}
              />
            </Tooltip>
            <Show when={meta()}>
              <span data-slot="text-part-meta" class="text-12-regular text-text-weak cursor-default">
                {meta()}
              </span>
            </Show>
          </div>
        </Show>
      </div>
    </Show>
  )
}

PART_MAPPING["reasoning"] = function ReasoningPartDisplay(props) {
  const part = () => props.part as ReasoningPart
  const streaming = createMemo(
    () => props.message.role === "assistant" && typeof (props.message as AssistantMessage).time.completed !== "number",
  )
  // A reasoning part can carry empty/undefined text (e.g. DeepSeek's injected
  // empty reasoning, or before text has streamed in) — guard it like
  // TextPartDisplay does, otherwise `.trim()` throws and crashes the session
  // view (hit when navigating into a subagent's child session).
  const text = () => (part().text ?? "").trim()
  // Same reasoning as TextPartDisplay — see isPlanMessage there.
  const linkArtifacts = createMemo(() => (props.message as AssistantMessage).agent !== "plan")

  return (
    <Show when={text()}>
      <div data-component="reasoning-part">
        <Show
          when={streaming()}
          fallback={
            <Markdown
              text={text()}
              cacheKey={part().id}
              streaming={false}
              linkArtifacts={linkArtifacts()}
              artifactFilenames={props.artifactFilenames}
            />
          }
        >
          <PacedMarkdown
            text={text()}
            cacheKey={part().id}
            streaming={streaming()}
            linkArtifacts={linkArtifacts()}
            artifactFilenames={props.artifactFilenames}
          />
        </Show>
      </div>
    </Show>
  )
}

ToolRegistry.register({
  name: "read",
  render(props) {
    const data = useData()
    const i18n = useI18n()
    const args: string[] = []
    if (props.input.offset) args.push("offset=" + props.input.offset)
    if (props.input.limit) args.push("limit=" + props.input.limit)
    const loaded = createMemo(() => {
      if (props.status !== "completed") return []
      const value = props.metadata.loaded
      if (!value || !Array.isArray(value)) return []
      return value.filter((p): p is string => typeof p === "string")
    })
    return (
      <>
        <BasicTool
          {...props}
          icon="file-glass"
          trigger={{
            title: i18n.t("ui.tool.read"),
            subtitle: props.input.filePath ? getFilename(props.input.filePath) : "",
            args,
          }}
        />
        <For each={loaded()}>
          {(filepath) => (
            <div data-component="tool-loaded-file">
              <Icon name="arrow-left" size="small" />
              <span>
                {i18n.t("ui.tool.loaded")} {relativizeProjectPath(filepath, data.directory)}
              </span>
            </div>
          )}
        </For>
      </>
    )
  },
})

ToolRegistry.register({
  name: "list",
  render(props) {
    const i18n = useI18n()
    return (
      <BasicTool
        {...props}
        icon="numbered-view"
        trigger={{ title: i18n.t("ui.tool.list"), subtitle: getDirectory(props.input.path || "/") }}
      >
        <Show when={props.output}>
          <div data-component="tool-output" data-scrollable>
            <Markdown text={props.output!} />
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "memory",
  render(props) {
    const i18n = useI18n()
    const data = useData()
    const [undone, setUndone] = createSignal(false)
    const [busy, setBusy] = createSignal(false)

    const note = createMemo(() => (typeof props.metadata?.note === "string" ? props.metadata.note : ""))
    const memoryId = createMemo(() => (typeof props.metadata?.memoryId === "string" ? props.metadata.memoryId : ""))
    // Only the compact chip when a note was actually saved; otherwise fall back
    // to the generic tool display (pending/error/"nothing to remember").
    const saved = createMemo(() => props.status === "completed" && props.metadata?.saved === true && !!memoryId())

    const handleUndo = async () => {
      const id = memoryId()
      if (!id || busy() || undone() || !data.forgetMemory) return
      setBusy(true)
      try {
        await data.forgetMemory(id)
        setUndone(true)
      } finally {
        setBusy(false)
      }
    }

    return (
      <Show
        when={saved()}
        fallback={
          <BasicTool {...props} icon="brain" trigger={{ title: i18n.t("ui.tool.memory") }}>
            <Show when={props.output}>
              <div data-component="tool-output">{props.output}</div>
            </Show>
          </BasicTool>
        }
      >
        <div data-component="memory-chip" data-undone={undone() ? "" : undefined}>
          <span data-slot="memory-chip-icon">🧠</span>
          <span data-slot="memory-chip-text">
            <Show when={!undone()} fallback={i18n.t("ui.tool.memory.undone")}>
              {i18n.t("ui.tool.memory.remembered")}: {note()}
            </Show>
          </span>
          <Show when={!undone() && !!data.forgetMemory}>
            <button data-slot="memory-chip-undo" disabled={busy()} onClick={handleUndo}>
              {i18n.t("ui.tool.memory.undo")}
            </button>
          </Show>
        </div>
      </Show>
    )
  },
})

ToolRegistry.register({
  name: "glob",
  render(props) {
    const i18n = useI18n()
    return (
      <BasicTool
        {...props}
        icon="file-glass"
        trigger={{
          title: i18n.t("ui.tool.glob"),
          subtitle: getDirectory(props.input.path || "/"),
          args: props.input.pattern ? ["pattern=" + props.input.pattern] : [],
        }}
      >
        <Show when={props.output}>
          <div data-component="tool-output" data-scrollable>
            <Markdown text={props.output!} />
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "grep",
  render(props) {
    const i18n = useI18n()
    const completed = createMemo(() => props.status === "completed")
    return (
      <BasicTool
        {...props}
        icon="magnifying-glass"
        trigger={{
          title: completed() ? i18n.t("ui.tool.search.completed") : i18n.t("ui.tool.grep"),
        }}
      >
        <ToolDetails
          rows={[
            { label: i18n.t("ui.tool.details.path"), value: getDirectory(props.input.path || "/") },
            { label: i18n.t("ui.tool.details.pattern"), value: props.input.pattern },
            { label: i18n.t("ui.tool.details.include"), value: props.input.include },
          ]}
        />
        <Show when={props.output}>
          <div data-component="tool-output" data-scrollable>
            <Markdown text={props.output!} />
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "webfetch",
  render(props) {
    const i18n = useI18n()
    const pending = createMemo(() => props.status === "pending" || props.status === "running")
    const completed = createMemo(() => props.status === "completed")
    const url = createMemo(() => {
      const value = props.input.url
      if (typeof value !== "string") return ""
      return value
    })
    return (
      <BasicTool
        {...props}
        icon="file-glass"
        trigger={{
          title: completed() ? i18n.t("ui.tool.webfetch.completed") : i18n.t("ui.tool.webfetch"),
        }}
      >
        <ToolDetails rows={[{ label: i18n.t("ui.tool.details.url"), value: url() }]} />
        <Show when={props.output}>
          <div data-component="tool-output" data-scrollable>
            <Markdown text={props.output!} />
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "websearch",
  render(props) {
    const i18n = useI18n()
    const completed = createMemo(() => props.status === "completed")
    const query = createMemo(() => {
      const value = props.input.query
      if (typeof value !== "string") return ""
      return value
    })

    return (
      <BasicTool
        {...props}
        icon="globe"
        trigger={{
          title: completed() ? i18n.t("ui.tool.search.completed") : i18n.t("ui.tool.websearch"),
        }}
      >
        <ToolDetails rows={[{ label: i18n.t("ui.tool.details.query"), value: query() }]} />
        <ExaOutput output={props.output} />
      </BasicTool>
    )
  },
})

// Structured code review: the tool call IS the report, so the card renders
// straight from the arguments the model sent (persisted in the part's
// metadata) rather than from the tool output, which is only a receipt.
ToolRegistry.register({
  name: "report_findings",
  render(props) {
    const findings = createMemo(() => {
      const value = props.metadata.findings
      if (Array.isArray(value)) return value as ReviewFinding[]
      // Mid-stream the metadata is still empty while the arguments are already
      // parsed — fall back to the input so the card fills in as it arrives.
      const input = props.input.findings
      return Array.isArray(input) ? (input as ReviewFinding[]) : []
    })
    const level = createMemo(() => {
      const value = props.metadata.level ?? props.input.level
      return typeof value === "string" ? value : undefined
    })
    const dropped = createMemo(() => (typeof props.metadata.dropped === "number" ? props.metadata.dropped : 0))

    return <ReviewFindings level={level()} findings={findings()} dropped={dropped()} status={props.status} />
  },
})

ToolRegistry.register({
  name: "task",
  render(props) {
    const data = useData()
    const i18n = useI18n()
    const location = useLocation()
    const childSessionId = createMemo(() => {
      const value = props.metadata.sessionId
      if (typeof value === "string" && value) return value
      return taskSession(props.input, location.pathname, data.store.session, data.store.agent)
    })
    const agent = createMemo(() => taskAgent(props.input.subagent_type, data.store.agent))
    const title = createMemo(() => agent().name ?? i18n.t("ui.tool.agent.default"))
    const completed = createMemo(() => props.status === "completed")
    const subtitle = createMemo(() => {
      const value = props.input.description
      if (typeof value === "string" && value) return value
      return childSessionId()
    })

    const href = createMemo(() => sessionLink(childSessionId(), location.pathname, data.sessionHref))
    const clickable = createMemo(() => !!(childSessionId() && (data.navigateToSession || href())))

    const open = () => {
      const id = childSessionId()
      if (!id) return
      if (data.navigateToSession) {
        data.navigateToSession(id)
        return
      }
      const value = href()
      if (value) window.location.assign(value)
    }

    const navigate = (event: MouseEvent) => {
      if (!data.navigateToSession) return
      if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      event.preventDefault()
      open()
    }

    return (
      <BasicTool
        icon="robot"
        status={props.status}
        trigger={{
          title: completed() ? i18n.t("ui.tool.task.completed") : title(),
          subtitle: completed() ? undefined : subtitle(),
        }}
        hideDetails={!completed()}
        triggerHref={completed() ? undefined : href()}
        clickable={!completed() && clickable()}
        onTriggerClick={completed() ? undefined : navigate}
      >
        <ToolDetails
          rows={[
            { label: i18n.t("ui.tool.details.agent"), value: title() },
            { label: i18n.t("ui.tool.details.task"), value: subtitle() },
            { label: i18n.t("ui.tool.details.session"), value: childSessionId() },
          ]}
        />
        <Show when={props.output}>
          <div data-component="tool-output" data-scrollable>
            <Markdown text={props.output!} />
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "bash",
  render(props) {
    const i18n = useI18n()
    const pending = () => props.status === "pending"
    const active = () => props.status === "pending" || props.status === "running"
    const sawActive = active()
    const command = createMemo(() => props.input.command ?? props.metadata.command ?? "")
    const description = createMemo(() => {
      if (typeof props.input.description === "string" && props.input.description.length > 0)
        return props.input.description
      if (typeof props.metadata.description === "string" && props.metadata.description.length > 0)
        return props.metadata.description
      if (typeof props.title === "string" && props.title.length > 0) return props.title
      return ""
    })
    const text = createMemo(() => {
      const out = stripAnsi(props.output || props.metadata.output || "")
      return `$ ${command()}${out ? "\n\n" + out : ""}`
    })
    const [copied, setCopied] = createSignal(false)
    const approval = createMemo(() => autoApprovalOf(props.metadata))

    const handleCopy = async () => {
      const content = text()
      if (!content) return
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    return (
      <BasicTool
        {...props}
        icon="terminal"
        trigger={
          <div data-slot="basic-tool-tool-info-structured">
            <div data-slot="basic-tool-tool-info-main">
              <Show
                when={!pending()}
                fallback={
                  <span data-slot="basic-tool-tool-title">
                    <TextShimmer text={i18n.t("ui.tool.shell")} active />
                  </span>
                }
              >
                <Show when={description()}>
                  {(value) => <ShellSubmessage text={value()} animate={sawActive} shimmer={active()} />}
                </Show>
              </Show>
              <Show when={approval()}>{(value) => <AutoApprovedMark approval={value()} />}</Show>
            </div>
          </div>
        }
      >
        <Show when={approval()}>
          {(value) => (
            <div data-slot="auto-approved-row">
              <AutoApprovedMark approval={value()} action />
            </div>
          )}
        </Show>
        <ToolDetails rows={[{ label: i18n.t("ui.tool.details.description"), value: description() }]} />
        <div data-component="bash-output">
          <div data-slot="bash-copy">
            <Tooltip value={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copy")} placement="top">
              <IconButton
                icon={copied() ? "check" : "copy"}
                size="small"
                variant="secondary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCopy}
                aria-label={copied() ? i18n.t("ui.message.copied") : i18n.t("ui.message.copy")}
              />
            </Tooltip>
          </div>
          <div data-slot="bash-scroll" data-scrollable>
            <pre data-slot="bash-pre">
              <code>{text()}</code>
            </pre>
          </div>
        </div>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "edit",
  render(props) {
    const i18n = useI18n()
    const fileComponent = useFileComponent()
    const diagnostics = createMemo(() => getDiagnostics(props.metadata.diagnostics, props.input.filePath))
    const path = createMemo(() => props.metadata?.filediff?.file || props.input.filePath || "")
    const filename = () => getFilename(props.input.filePath ?? "")
    const pending = () => props.status === "pending" || props.status === "running"
    const completed = createMemo(() => props.status === "completed")
    const content = createMemo(() => {
      const after = props.metadata?.filediff?.after
      if (typeof after === "string") return after
      if (typeof props.input.newString === "string") return props.input.newString
      return ""
    })
    return (
      <div data-component="edit-tool">
        <BasicTool
          {...props}
          icon="edit-outline"
          defer
          trigger={
            <div data-component="edit-trigger" data-completed={completed() ? "true" : undefined}>
              <div data-slot="message-part-title-area">
                <div data-slot="message-part-title">
                  <span data-slot="message-part-title-text">
                    <TextShimmer
                      text={
                        completed()
                          ? i18n.t("ui.messagePart.title.edit.completed")
                          : i18n.t("ui.messagePart.title.edit")
                      }
                      active={pending()}
                    />
                  </span>
                  <Show when={!pending() && !completed()}>
                    <span data-slot="message-part-title-filename">{filename()}</span>
                  </Show>
                </div>
                <Show when={!pending() && !completed() && props.input.filePath?.includes("/")}>
                  <div data-slot="message-part-path">
                    <span data-slot="message-part-directory">{getDirectory(props.input.filePath!)}</span>
                  </div>
                </Show>
              </div>
              <div data-slot="message-part-actions">
                <Show when={!pending() && !completed() && props.metadata.filediff}>
                  <DiffChanges changes={props.metadata.filediff} />
                </Show>
              </div>
            </div>
          }
        >
          <Show
            when={completed()}
            fallback={
              <Show when={path()}>
                <ToolFileAccordion
                  path={path()}
                  actions={
                    <Show when={!pending() && props.metadata.filediff}>
                      <DiffChanges changes={props.metadata.filediff!} />
                    </Show>
                  }
                >
                  <div data-component="edit-content">
                    <Dynamic
                      component={fileComponent}
                      mode="diff"
                      before={{
                        name: props.metadata?.filediff?.file || props.input.filePath,
                        contents: props.metadata?.filediff?.before || props.input.oldString,
                      }}
                      after={{
                        name: props.metadata?.filediff?.file || props.input.filePath,
                        contents: props.metadata?.filediff?.after || props.input.newString,
                      }}
                    />
                  </div>
                </ToolFileAccordion>
              </Show>
            }
          >
            <Show when={content()}>
              <div data-component="tool-output" data-scrollable>
                {content()}
              </div>
            </Show>
          </Show>
          <DiagnosticsDisplay diagnostics={diagnostics()} />
        </BasicTool>
      </div>
    )
  },
})

ToolRegistry.register({
  name: "write",
  render(props) {
    const i18n = useI18n()
    const fileComponent = useFileComponent()
    const diagnostics = createMemo(() => getDiagnostics(props.metadata.diagnostics, props.input.filePath))
    const path = createMemo(() => props.input.filePath || "")
    const content = createMemo(() => (typeof props.input.content === "string" ? props.input.content : ""))
    const filename = () => getFilename(props.input.filePath ?? "")
    const pending = () => props.status === "pending" || props.status === "running"
    const completed = createMemo(() => props.status === "completed")
    return (
      <div data-component="write-tool">
        <BasicTool
          {...props}
          icon="edit-outline"
          defer
          trigger={
            <div data-component="write-trigger" data-completed={completed() ? "true" : undefined}>
              <div data-slot="message-part-title-area">
                <div data-slot="message-part-title">
                  <span data-slot="message-part-title-text">
                    <TextShimmer
                      text={
                        completed()
                          ? i18n.t("ui.messagePart.title.write.completed")
                          : i18n.t("ui.messagePart.title.write")
                      }
                      active={pending()}
                    />
                  </span>
                  <Show when={!pending() && !completed()}>
                    <span data-slot="message-part-title-filename">{filename()}</span>
                  </Show>
                </div>
                <Show when={!pending() && !completed() && props.input.filePath?.includes("/")}>
                  <div data-slot="message-part-path">
                    <span data-slot="message-part-directory">{getDirectory(props.input.filePath!)}</span>
                  </div>
                </Show>
              </div>
              <div data-slot="message-part-actions">{/* <DiffChanges diff={diff} /> */}</div>
            </div>
          }
        >
          <Show
            when={completed()}
            fallback={
              <Show when={content() && path()}>
                <ToolFileAccordion path={path()}>
                  <div data-component="write-content">
                    <Dynamic
                      component={fileComponent}
                      mode="text"
                      file={{
                        name: props.input.filePath,
                        contents: content(),
                        cacheKey: checksum(content()),
                      }}
                      overflow="scroll"
                    />
                  </div>
                </ToolFileAccordion>
              </Show>
            }
          >
            <Show when={content()}>
              <div data-component="tool-output" data-scrollable>
                {content()}
              </div>
            </Show>
          </Show>
          <DiagnosticsDisplay diagnostics={diagnostics()} />
        </BasicTool>
      </div>
    )
  },
})

ToolRegistry.register({
  name: "apply_patch",
  render(props) {
    const i18n = useI18n()
    const fileComponent = useFileComponent()
    const files = createMemo(() => patchFiles(props.metadata.files))
    const pending = createMemo(() => props.status === "pending" || props.status === "running")
    const single = createMemo(() => {
      const list = files()
      if (list.length !== 1) return
      return list[0]
    })
    const fileContent = (file: NonNullable<ReturnType<typeof single>>) =>
      file.type === "delete" ? diffText(file.view, "deletions") : diffText(file.view, "additions")
    const [expanded, setExpanded] = createSignal<string[]>([])
    let seeded = false

    createEffect(() => {
      const list = files()
      if (list.length === 0) return
      if (seeded) return
      seeded = true
      setExpanded(list.filter((f) => f.type !== "delete").map((f) => f.filePath))
    })

    const subtitle = createMemo(() => {
      const count = files().length
      if (count === 0) return ""
      return `${count} ${i18n.t(count > 1 ? "ui.common.file.other" : "ui.common.file.one")}`
    })

    return (
      <Show
        when={single()}
        fallback={
          <div data-component="apply-patch-tool">
            <BasicTool
              {...props}
              icon="wrench"
              defer
              trigger={{
                title: props.status === "completed" ? i18n.t("ui.tool.patch.completed") : i18n.t("ui.tool.patch"),
                subtitle: props.status === "completed" ? undefined : subtitle(),
              }}
            >
              <Show
                when={props.status === "completed"}
                fallback={
                  <Show when={files().length > 0}>
                    <Accordion
                      multiple
                      data-scope="apply-patch"
                      style={{ "--sticky-accordion-offset": "calc(32px + var(--tool-content-gap))" }}
                      value={expanded()}
                      onChange={(value) => setExpanded(Array.isArray(value) ? value : value ? [value] : [])}
                    >
                      <For each={files()}>
                        {(file) => {
                          const active = createMemo(() => expanded().includes(file.filePath))
                          const [visible, setVisible] = createSignal(false)

                          createEffect(() => {
                            if (!active()) {
                              setVisible(false)
                              return
                            }

                            requestAnimationFrame(() => {
                              if (!active()) return
                              setVisible(true)
                            })
                          })

                          return (
                            <Accordion.Item value={file.filePath} data-type={file.type}>
                              <StickyAccordionHeader>
                                <Accordion.Trigger>
                                  <div data-slot="apply-patch-trigger-content">
                                    <div data-slot="apply-patch-file-info">
                                      <FileIcon node={{ path: file.relativePath, type: "file" }} />
                                      <div data-slot="apply-patch-file-name-container">
                                        <Show when={file.relativePath.includes("/")}>
                                          <span data-slot="apply-patch-directory">{`\u202A${getDirectory(file.relativePath)}\u202C`}</span>
                                        </Show>
                                        <span data-slot="apply-patch-filename">{getFilename(file.relativePath)}</span>
                                      </div>
                                    </div>
                                    <div data-slot="apply-patch-trigger-actions">
                                      <Switch>
                                        <Match when={file.type === "add"}>
                                          <span data-slot="apply-patch-change" data-type="added">
                                            {i18n.t("ui.patch.action.created")}
                                          </span>
                                        </Match>
                                        <Match when={file.type === "delete"}>
                                          <span data-slot="apply-patch-change" data-type="removed">
                                            {i18n.t("ui.patch.action.deleted")}
                                          </span>
                                        </Match>
                                        <Match when={file.type === "move"}>
                                          <span data-slot="apply-patch-change" data-type="modified">
                                            {i18n.t("ui.patch.action.moved")}
                                          </span>
                                        </Match>
                                        <Match when={true}>
                                          <DiffChanges
                                            changes={{ additions: file.additions, deletions: file.deletions }}
                                          />
                                        </Match>
                                      </Switch>
                                      <Icon name="chevron-grabber-vertical" size="small" />
                                    </div>
                                  </div>
                                </Accordion.Trigger>
                              </StickyAccordionHeader>
                              <Accordion.Content>
                                <Show when={visible()}>
                                  <div data-component="apply-patch-file-diff">
                                    <Dynamic component={fileComponent} mode="diff" fileDiff={file.view.fileDiff} />
                                  </div>
                                </Show>
                              </Accordion.Content>
                            </Accordion.Item>
                          )
                        }}
                      </For>
                    </Accordion>
                  </Show>
                }
              >
                <For each={files()}>
                  {(file) => (
                    <Show when={fileContent(file)}>
                      <div data-component="tool-output" data-scrollable>
                        {fileContent(file)}
                      </div>
                    </Show>
                  )}
                </For>
              </Show>
            </BasicTool>
          </div>
        }
      >
        <div data-component="apply-patch-tool">
          <BasicTool
            {...props}
            icon="wrench"
            defer
            trigger={
              <div data-component="edit-trigger" data-completed={props.status === "completed" ? "true" : undefined}>
                <div data-slot="message-part-title-area">
                  <div data-slot="message-part-title">
                    <span data-slot="message-part-title-text">
                      <TextShimmer
                        text={
                          props.status === "completed" ? i18n.t("ui.tool.patch.completed") : i18n.t("ui.tool.patch")
                        }
                        active={pending()}
                      />
                    </span>
                    <Show when={!pending() && props.status !== "completed"}>
                      <span data-slot="message-part-title-filename">{getFilename(single()!.relativePath)}</span>
                    </Show>
                  </div>
                  <Show when={!pending() && props.status !== "completed" && single()!.relativePath.includes("/")}>
                    <div data-slot="message-part-path">
                      <span data-slot="message-part-directory">{getDirectory(single()!.relativePath)}</span>
                    </div>
                  </Show>
                </div>
                <div data-slot="message-part-actions">
                  <Show when={!pending() && props.status !== "completed"}>
                    <DiffChanges changes={{ additions: single()!.additions, deletions: single()!.deletions }} />
                  </Show>
                </div>
              </div>
            }
          >
            <Show
              when={props.status === "completed"}
              fallback={
                <ToolFileAccordion
                  path={single()!.relativePath}
                  actions={
                    <Switch>
                      <Match when={single()!.type === "add"}>
                        <span data-slot="apply-patch-change" data-type="added">
                          {i18n.t("ui.patch.action.created")}
                        </span>
                      </Match>
                      <Match when={single()!.type === "delete"}>
                        <span data-slot="apply-patch-change" data-type="removed">
                          {i18n.t("ui.patch.action.deleted")}
                        </span>
                      </Match>
                      <Match when={single()!.type === "move"}>
                        <span data-slot="apply-patch-change" data-type="modified">
                          {i18n.t("ui.patch.action.moved")}
                        </span>
                      </Match>
                      <Match when={true}>
                        <DiffChanges changes={{ additions: single()!.additions, deletions: single()!.deletions }} />
                      </Match>
                    </Switch>
                  }
                >
                  <div data-component="apply-patch-file-diff">
                    <Dynamic component={fileComponent} mode="diff" fileDiff={single()!.view.fileDiff} />
                  </div>
                </ToolFileAccordion>
              }
            >
              <div data-component="tool-output" data-scrollable>
                {fileContent(single()!)}
              </div>
            </Show>
          </BasicTool>
        </div>
      </Show>
    )
  },
})

ToolRegistry.register({
  name: "todowrite",
  render(props) {
    const i18n = useI18n()
    const todos = createMemo(() => {
      const meta = props.metadata?.todos
      if (Array.isArray(meta)) return meta

      const input = props.input.todos
      if (Array.isArray(input)) return input

      return []
    })

    const subtitle = createMemo(() => {
      const list = todos()
      if (list.length === 0) return ""
      return `${list.filter((t: Todo) => t.status === "completed").length}/${list.length}`
    })

    return (
      <BasicTool
        {...props}
        defaultOpen
        icon="checklist"
        trigger={{
          title: i18n.t("ui.tool.todos"),
          subtitle: subtitle(),
        }}
      >
        <Show when={todos().length}>
          <div data-component="todos">
            <For each={todos()}>
              {(todo: Todo) => (
                <Checkbox readOnly checked={todo.status === "completed"}>
                  <span
                    data-slot="message-part-todo-content"
                    data-completed={todo.status === "completed" ? "completed" : undefined}
                  >
                    {todo.content}
                  </span>
                </Checkbox>
              )}
            </For>
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "question",
  render(props) {
    const i18n = useI18n()
    const questions = createMemo(() => (props.input.questions ?? []) as QuestionInfo[])
    const answers = createMemo(() => (props.metadata.answers ?? []) as QuestionAnswer[])
    // A non-blocking question completed the moment it was shown, with no
    // answers; the reply route stamps them in later (question/async.ts), so
    // `completed` keeps meaning "the user answered" for both kinds.
    const async = createMemo(() => props.metadata.async === true)
    const dismissed = createMemo(() => async() && props.metadata.dismissed === true)
    const completed = createMemo(() => answers().length > 0)

    const subtitle = createMemo(() => {
      const count = questions().length
      if (count === 0) return ""
      if (completed()) return i18n.t("ui.question.subtitle.answered", { count })
      if (dismissed()) return i18n.t("ui.messagePart.questions.dismissed")
      if (async()) return i18n.t("ui.question.async.waiting")
      return `${count} ${i18n.t(count > 1 ? "ui.common.question.other" : "ui.common.question.one")}`
    })

    const title = createMemo(() => {
      if (!completed()) return i18n.t("ui.tool.questions")
      return i18n.t(async() ? "ui.question.async.answerSent" : "ui.tool.questions.answered")
    })

    return (
      <BasicTool
        {...props}
        defaultOpen={completed() || async()}
        icon="ear"
        trigger={{
          title: title(),
          subtitle: completed() ? undefined : subtitle(),
        }}
      >
        <Show when={completed() || async()}>
          <div data-component="question-answers">
            <For each={questions()}>
              {(q, i) => {
                const answer = () => answers()[i()] ?? []
                return (
                  <div data-slot="question-answer-item">
                    <div data-slot="question-text">{q.question}</div>
                    <Show when={completed()}>
                      <div data-slot="answer-text">{answer().join(", ") || i18n.t("ui.question.answer.none")}</div>
                    </Show>
                  </div>
                )
              }}
            </For>
          </div>
        </Show>
      </BasicTool>
    )
  },
})

ToolRegistry.register({
  name: "skill",
  render(props) {
    const i18n = useI18n()
    const name = createMemo(() => props.input.name || i18n.t("ui.tool.skill"))
    const title = createMemo(() => {
      if (props.status === "completed") return i18n.t("ui.tool.skill.used", { skill: name() })
      if (props.status === "pending") return i18n.t("ui.tool.skill.preparing", { skill: name() })
      return i18n.t("ui.tool.skill.using", { skill: name() })
    })
    const running = createMemo(() => props.status === "pending" || props.status === "running")
    const titleContent = () => <TextShimmer text={title()} active={running()} />

    const trigger = () => (
      <div data-slot="basic-tool-tool-info-structured">
        <div data-slot="basic-tool-tool-info-main">
          <span data-slot="basic-tool-tool-title" class="agent-title">
            {titleContent()}
          </span>
        </div>
      </div>
    )

    return (
      <BasicTool
        icon={props.status === "completed" ? "education" : "book-open"}
        status={props.status}
        trigger={trigger()}
        hideDetails
      />
    )
  },
})
